/**
 * Ilim Lab - Premium Commercial Application
 * Современный JavaScript с интерактивными эффектами и анимациями
 */

class PhysicsLabApp {
  constructor() {
    this.currentPage = null;
    this.theme = localStorage.getItem('theme') || 'dark';
    this.language = localStorage.getItem('language') || 'ru';
    this.isInitialized = false;
    this.isScrolled = false;
    this.currentSimulation = null;
    this.i18n = null;
    this.isFullscreenMode = false;
    this.originalCanvasContainer = null;
    this.originalControlsContainer = null;
    this.isHandlingRoute = false; // Защита от повторного вызова handleRouteChange
    this.lastHandledHash = null; // Последний обработанный hash
    this.isMobile = this.detectMobile();
    this.mobileMenuOpen = false;
  }

  // Определение мобильного устройства
  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768) ||
           ('ontouchstart' in window);
  }

  // Мобильное меню
  initMobileMenu() {
    const menuToggle = document.getElementById('menu-toggle');
    const mobileMenu = document.getElementById('mobile-menu');
    const closeMenu = document.getElementById('close-menu');
    const mobileThemeToggle = document.getElementById('mobile-theme-toggle');
    const mobileLangSelect = document.getElementById('mobile-lang-select');

    if (menuToggle) {
      menuToggle.addEventListener('click', () => this.toggleMobileMenu());
    }

    if (closeMenu) {
      closeMenu.addEventListener('click', () => this.closeMobileMenu());
    }

    if (mobileThemeToggle) {
      mobileThemeToggle.addEventListener('click', () => this.toggleTheme());
    }

    if (mobileLangSelect) {
      mobileLangSelect.addEventListener('change', (e) => this.changeLanguage(e.target.value));
    }

    // Закрытие меню при клике вне его
    if (mobileMenu) {
      mobileMenu.addEventListener('click', (e) => {
        if (e.target === mobileMenu) {
          this.closeMobileMenu();
        }
      });
    }

    // Закрытие меню при изменении размера окна
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        this.closeMobileMenu();
      }
    });
  }

  toggleMobileMenu() {
    const menuToggle = document.getElementById('menu-toggle');
    const mobileMenu = document.getElementById('mobile-menu');
    
    this.mobileMenuOpen = !this.mobileMenuOpen;
    
    if (menuToggle) {
      menuToggle.classList.toggle('active', this.mobileMenuOpen);
    }
    
    if (mobileMenu) {
      mobileMenu.classList.toggle('active', this.mobileMenuOpen);
    }
    
    // Блокируем скролл при открытом меню
    document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
  }

  closeMobileMenu() {
    const menuToggle = document.getElementById('menu-toggle');
    const mobileMenu = document.getElementById('mobile-menu');
    
    this.mobileMenuOpen = false;
    
    if (menuToggle) {
      menuToggle.classList.remove('active');
    }
    
    if (mobileMenu) {
      mobileMenu.classList.remove('active');
    }
    
    document.body.style.overflow = '';
  }

  async loadSimulationPlayer(simulationId) {
    console.log('🎬 loadSimulationPlayer вызван с ID:', simulationId);
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
      console.error('❌ mainContent не найден');
      return;
    }
    console.log('✅ mainContent найден');

    // Закрываем мобильное меню если открыто
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }

    // Скрываем глобальный UI для чистого плеера
    const headerEl = document.querySelector('.header');
    const footerEl = document.querySelector('.footer');
    const mobileMenuEl = document.getElementById('mobile-menu');
    const prevHeaderDisplay = headerEl ? headerEl.style.display : undefined;
    const prevFooterDisplay = footerEl ? footerEl.style.display : undefined;
    const prevMobileMenuDisplay = mobileMenuEl ? mobileMenuEl.style.display : undefined;
    const prevBodyOverflow = document.body.style.overflow;
    if (headerEl) headerEl.style.display = 'none';
    if (footerEl) footerEl.style.display = 'none';
    if (mobileMenuEl) mobileMenuEl.style.display = 'none';
    document.body.style.overflow = 'hidden';

    // Minimal chrome-less player layout
    document.title = 'Ilim Lab Player';
    mainContent.innerHTML = `
      <div class="w-full h-screen flex flex-col bg-black">
        <div class="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-black text-gray-200">
          <div class="flex items-center gap-2">
            <button id="player-back" class="btn btn-secondary btn-sm">Закрыть</button>
            <span class="text-sm text-secondary">${simulationId}</span>
          </div>
          <div class="flex items-center gap-2">
            <button id="player-fullscreen" class="btn btn-secondary btn-sm">На весь экран</button>
          </div>
        </div>
        <div class="flex-1 bg-black">
          <canvas id="simulation-canvas" class="w-full h-full block"></canvas>
        </div>
      </div>
    `;

    const canvas = document.getElementById('simulation-canvas');
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(320, Math.floor(rect.width));
      canvas.height = Math.max(240, Math.floor(rect.height));
    };
    resize();
    window.addEventListener('resize', resize);

    if (simulationId === 'ohms-law') {
      console.log('🔧 Инициализируем симуляцию закона Ома в плеере...');
      try {
        const module = await import('./sims/ohms-law.js');
        console.log('✅ Модуль симуляции загружен');
        const OhmsLawSimulation = module.default;
        this.currentSimulation = new OhmsLawSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n
        });
        console.log('✅ Симуляция создана');
        if (this.currentSimulation && this.currentSimulation.init) {
          console.log('🚀 Запускаем симуляцию...');
          this.currentSimulation.init();
          console.log('✅ Симуляция запущена');
        }
      } catch (e) {
        console.error('❌ Не удалось запустить плеер симуляции:', e);
      }
    }

    const restoreUI = () => {
      if (headerEl) headerEl.style.display = prevHeaderDisplay ?? '';
      if (footerEl) footerEl.style.display = prevFooterDisplay ?? '';
      if (mobileMenuEl) mobileMenuEl.style.display = prevMobileMenuDisplay ?? '';
      document.body.style.overflow = prevBodyOverflow ?? '';
    };

    const backBtn = document.getElementById('player-back');
    if (backBtn) backBtn.addEventListener('click', () => { restoreUI(); window.history.back(); });

    const fsBtn = document.getElementById('player-fullscreen');
    if (fsBtn) fsBtn.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        canvas.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    });

    // Если пользователь уйдет по hashchange с /player/, вернуть UI
    const onHashChange = () => {
      const h = window.location.hash.slice(1);
      if (!h.startsWith('/player/')) {
        restoreUI();
        window.removeEventListener('hashchange', onHashChange);
      }
    };
    window.addEventListener('hashchange', onHashChange);
  }

  initInterferenceWavesSimulation() {
    const canvas = document.getElementById('waves-canvas');
    if (!canvas) {
      console.error('Canvas для interference-waves не найден');
      return;
    }

    import('./sims/interference-waves.js').then(module => {
      try {
        const InterferenceWavesSimulation = module.default;
        			this.currentSimulation = new InterferenceWavesSimulation(canvas, {
				i18n: this.i18n
			});

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('waves-start');
        const resetBtn = document.getElementById('waves-reset');
        const modeSel = document.getElementById('waves-mode');
        const lambdaRange = document.getElementById('waves-lambda');
        const lambdaNum = document.getElementById('waves-lambda-num');
        const freqRange = document.getElementById('waves-frequency');
        const freqNum = document.getElementById('waves-frequency-num');
        const ampRange = document.getElementById('waves-amplitude');
        const ampNum = document.getElementById('waves-amplitude-num');
        const speedRange = document.getElementById('waves-speed');
        const speedNum = document.getElementById('waves-speed-num');
        const phaseChk = document.getElementById('waves-show-phase');
        const nodesChk = document.getElementById('waves-show-nodes');
        const sourcesChk = document.getElementById('waves-show-sources');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) this.updateStartButtonState(startBtn, false);
          });
        }

        if (modeSel) {
          modeSel.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('mode', e.target.value);
          });
        }

        const syncLambda = (val) => {
          lambdaRange.value = String(val);
          lambdaNum.value = String(val);
          this.currentSimulation.updateParameter('wavelength', Number(val));
        };
        if (lambdaRange && lambdaNum) {
          lambdaRange.addEventListener('input', (e) => syncLambda(e.target.value));
          lambdaNum.addEventListener('change', (e) => syncLambda(e.target.value));
        }

        const syncFreq = (val) => {
          freqRange.value = String(val);
          freqNum.value = String(val);
          this.currentSimulation.updateParameter('frequency', Number(val));
        };
        if (freqRange && freqNum) {
          freqRange.addEventListener('input', (e) => syncFreq(e.target.value));
          freqNum.addEventListener('change', (e) => syncFreq(e.target.value));
        }

        const syncAmp = (val) => {
          ampRange.value = String(val);
          ampNum.value = String(val);
          this.currentSimulation.updateParameter('amplitude', Number(val));
        };
        if (ampRange && ampNum) {
          ampRange.addEventListener('input', (e) => syncAmp(e.target.value));
          ampNum.addEventListener('change', (e) => syncAmp(e.target.value));
        }

        const syncSpeed = (val) => {
          speedRange.value = String(val);
          speedNum.value = String(val);
          this.currentSimulation.updateParameter('waveSpeed', Number(val));
        };
        if (speedRange && speedNum) {
          speedRange.addEventListener('input', (e) => syncSpeed(e.target.value));
          speedNum.addEventListener('change', (e) => syncSpeed(e.target.value));
        }

        if (phaseChk) {
          phaseChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showPhase', e.target.checked);
          });
        }

        if (nodesChk) {
          nodesChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showNodes', e.target.checked);
          });
        }

        if (sourcesChk) {
          sourcesChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showSources', e.target.checked);
          });
        }

        // Автозапуск
        try { this.currentSimulation.start(); if (startBtn) this.updateStartButtonState(startBtn, true); } catch {}

      } catch (error) {
        console.error('Ошибка при инициализации interference-waves:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля interference-waves:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initGravityOrbitsP5Simulation() {
    const canvas = document.getElementById('simulation-canvas');
    if (!canvas) {
      console.error('Canvas элемент не найден');
      return;
    }

    console.log('🌍 Инициализация симуляции гравитации и орбит...');
    
    // Импортируем и создаем симуляцию
    import('./sims/gravity-orbits.js').then(module => {
      try {
        const GravityOrbitsSimulation = module.default;
        console.log('✅ Модуль симуляции загружен');
        
        this.currentSimulation = new GravityOrbitsSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n
        });
        
        console.log('✅ Симуляция создана успешно');

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        // Добавляем обработчики кнопок
        const startButton = document.getElementById('start-simulation');
        const pauseButton = document.getElementById('pause-simulation');
        const resetButton = document.getElementById('reset-simulation');

        if (startButton) {
          startButton.addEventListener('click', () => {
            if (this.currentSimulation) {
              if (this.currentSimulation.state.isRunning) {
                this.currentSimulation.pause();
                startButton.innerHTML = `
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span data-i18n="simulation.start"></span>
                `;
              } else {
                this.currentSimulation.start();
                startButton.innerHTML = `
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span data-i18n="simulation.pause"></span>
                `;
              }
            }
          });
        }

        if (resetButton) {
          resetButton.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.reset();
            }
          });
        }

        // Добавляем обработчики параметров
        const timeScaleRange = document.getElementById('gravity-time-scale');
        const timeScaleNum = document.getElementById('gravity-time-scale-num');
        const showVectorsChk = document.getElementById('gravity-show-vectors');
        const showOrbitsChk = document.getElementById('gravity-show-orbits');
        const showGridChk = document.getElementById('gravity-show-grid');
        const showInfoChk = document.getElementById('gravity-show-info');
        const gravityEnabledChk = document.getElementById('gravity-enabled');
        const collisionsChk = document.getElementById('gravity-collisions');

        const syncTimeScale = (val) => {
          timeScaleRange.value = String(val);
          timeScaleNum.value = String(val);
          this.currentSimulation.updateParameter('timeScale', Number(val));
        };
        if (timeScaleRange && timeScaleNum) {
          timeScaleRange.addEventListener('input', (e) => syncTimeScale(e.target.value));
          timeScaleNum.addEventListener('change', (e) => syncTimeScale(e.target.value));
        }

        if (showVectorsChk) {
          showVectorsChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showVectors', e.target.checked);
          });
        }

        if (showOrbitsChk) {
          showOrbitsChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showOrbits', e.target.checked);
          });
        }

        if (showGridChk) {
          showGridChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showGrid', e.target.checked);
          });
        }

        if (showInfoChk) {
          showInfoChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('showInfo', e.target.checked);
          });
        }

        if (gravityEnabledChk) {
          gravityEnabledChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('gravityEnabled', e.target.checked);
          });
        }

        if (collisionsChk) {
          collisionsChk.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('collisionsEnabled', e.target.checked);
          });
        }

        // Обработчики кнопок добавления тел
        const addSunBtn = document.getElementById('add-sun');
        const addEarthBtn = document.getElementById('add-earth');
        const addMoonBtn = document.getElementById('add-moon');
        const clearBodiesBtn = document.getElementById('clear-bodies');

        if (addSunBtn) {
          addSunBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.startPlacing('sun');
            }
          });
        }

        if (addEarthBtn) {
          addEarthBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.startPlacing('earth');
            }
          });
        }

        if (addMoonBtn) {
          addMoonBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.startPlacing('moon');
            }
          });
        }

        if (clearBodiesBtn) {
          clearBodiesBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.clearBodies();
            }
          });
        }

        console.log('✅ Симуляция гравитации и орбит инициализирована успешно');
        
      } catch (error) {
        console.error('❌ Ошибка инициализации симуляции гравитации и орбит (P5):', error);
      }
    }).catch(error => {
      console.error('❌ Ошибка загрузки модуля симуляции гравитации и орбит (P5):', error);
    });
  }

  async init() {
    try {
      // Обновляем кнопку авторизации
      await this.updateAuthButton();
              console.log('🚀 Инициализация Ilim Lab...');
      
      // Инициализируем i18n систему
      await this.initI18n();
      
      this.setupEventListeners();
      this.initEvents();
      this.initMobileMenu();
      this.applyTheme();
      this.applyLanguage();
      this.setupScrollEffects();
      await this.hideLoadingScreen();
      await this.loadHomePage();
      
      this.isInitialized = true;
              console.log('✅ Ilim Lab успешно инициализирован');
      
      // Создаем глобальную функцию для onclick атрибутов
      window.navigateTo = (path, scrollToId = null) => {
        if (this.isInitialized) {
          this.navigateTo(path, scrollToId);
        } else {
          console.warn('⚠️ Приложение еще не инициализировано, откладываем навигацию');
          setTimeout(() => this.navigateTo(path, scrollToId), 100);
        }
      };
      
    } catch (error) {
              console.error('❌ Ошибка инициализации Ilim Lab:', error);
      this.showErrorScreen(error);
    }
  }

  async initI18n() {
    try {
      // Импортируем упрощенную i18n систему
      const i18nModule = await import('./js/i18n-simple.js');
      const initI18n = i18nModule.default;
      this.i18n = initI18n();
      
      // Устанавливаем текущий язык
      this.i18n.setLanguage(this.language);
      
      console.log('✅ i18n система инициализирована');
    } catch (error) {
      console.error('❌ Ошибка инициализации i18n:', error);
      // Создаем простую заглушку
      this.i18n = {
        setLanguage: (lang) => { this.language = lang; },
        t: (key) => key,
        getLanguage: () => this.language
      };
    }
  }

  setupEventListeners() {
    try {
      // Theme toggle
      const themeToggle = document.getElementById('theme-toggle');
      const mobileThemeToggle = document.getElementById('mobile-theme-toggle');
      if (themeToggle) themeToggle.addEventListener('click', () => this.handleThemeToggle());
      if (mobileThemeToggle) mobileThemeToggle.addEventListener('click', () => this.handleThemeToggle());
      
      // Language selectors
      const langSelect = document.getElementById('lang-select');
      const mobileLangSelect = document.getElementById('mobile-lang-select');
      if (langSelect) langSelect.addEventListener('change', (e) => this.handleLanguageChange(e.target.value));
      if (mobileLangSelect) mobileLangSelect.addEventListener('change', (e) => this.handleLanguageChange(e.target.value));
      
      // Mobile menu
      const menuToggle = document.getElementById('menu-toggle');
      const closeMenu = document.getElementById('close-menu');
      if (menuToggle) menuToggle.addEventListener('click', () => this.handleMobileMenu());
      if (closeMenu) closeMenu.addEventListener('click', () => this.handleMobileMenu());
      
      // Window resize handler for canvas responsiveness
      window.addEventListener('resize', () => this.handleCanvasResize());
      
      // Navigation links
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-route]');
        if (link) {
          e.preventDefault();
          const route = link.getAttribute('data-route');
          this.navigateTo(route);
          
          // Close mobile menu if open
          const mobileMenu = document.getElementById('mobile-menu');
          if (mobileMenu && mobileMenu.classList.contains('open')) {
            mobileMenu.classList.remove('open');
          }
        }
        
        // Обработка ссылок с onclick (например, в хедере)
        const linkWithOnclick = e.target.closest('a[onclick*="navigateTo"]');
        if (linkWithOnclick) {
          e.preventDefault();
          const onclick = linkWithOnclick.getAttribute('onclick');
          // Поддерживаем как navigateTo('route'), так и navigateTo('route', 'scrollToId')
          const match = onclick.match(/navigateTo\('([^']+)'(?:,\s*'([^']+)')?\)/);
          if (match) {
            const route = match[1];
            const scrollToId = match[2] || null;
            this.navigateTo(route, scrollToId);
          }
        }
        
        // Обработка кнопок с onclick
        const button = e.target.closest('button[onclick*="navigateTo"]');
        if (button) {
          e.preventDefault();
          const onclick = button.getAttribute('onclick');
          // Поддерживаем как navigateTo('route'), так и navigateTo('route', 'scrollToId')
          const match = onclick.match(/navigateTo\('([^']+)'(?:,\s*'([^']+)')?\)/);
          if (match) {
            const route = match[1];
            const scrollToId = match[2] || null;
            this.navigateTo(route, scrollToId);
          }
        }
      });
      
      // Hash change
      window.addEventListener('hashchange', () => this.handleRouteChange());
      
      // Scroll events
      window.addEventListener('scroll', () => this.handleScroll());
      
      console.log('✅ Event listeners настроены');
    } catch (error) {
      console.error('❌ Ошибка настройки event listeners:', error);
    }
  }
  
  // Handle canvas resize for mobile responsiveness
  handleCanvasResize() {
    const canvases = document.querySelectorAll('canvas');
    canvases.forEach(canvas => {
      const container = canvas.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        const newWidth = rect.width;
        const newHeight = rect.height;
        
        // Only resize if dimensions actually changed
        if (canvas.width !== newWidth || canvas.height !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Notify simulation if it exists
          if (this.currentSimulation && typeof this.currentSimulation.handleResize === 'function') {
            this.currentSimulation.handleResize(newWidth, newHeight);
          }
          
          // Re-render if simulation has render method
          if (this.currentSimulation && typeof this.currentSimulation.render === 'function') {
            this.currentSimulation.render();
          }
        }
      }
    });
  }

  // Helper method to update start/stop button state consistently
  updateStartButtonState(button, isRunning) {
    if (!button) return;
    
    if (isRunning) {
      button.innerHTML = `
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <span data-i18n="simulation.pause"></span>
      `;
    } else {
      button.innerHTML = `
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span data-i18n="simulation.start"></span>
      `;
    }
    
    // Update translations for the button immediately
    this.updatePageTranslations();
  }

  setupScrollEffects() {
    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // entry.target.classList.add('animate-fade-in-up'); // Отключено для быстрой загрузки
          
          // Add floating animation to feature icons - ВРЕМЕННО ОТКЛЮЧЕНО для устранения проблем с масштабированием
          // if (entry.target.classList.contains('feature-card')) {
          //   const icon = entry.target.querySelector('.feature-icon');
          //   if (icon) {
          //     setTimeout(() => icon.classList.add('animate-float'), 300);
          //   }
          // }
          
          // Add pulse glow to simulation cards - ВРЕМЕННО ОТКЛЮЧЕНО для устранения проблемы с масштабированием
          // if (entry.target.classList.contains('simulation-card')) {
          //   setTimeout(() => entry.target.classList.add('animate-pulse-glow'), 500);
          // }
        }
      });
    }, observerOptions);

    // Observe all cards and sections
    document.querySelectorAll('.feature-card, .simulation-card, .section-header').forEach(el => {
      observer.observe(el);
    });
  }

  handleScroll() {
    const header = document.querySelector('.header');
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (scrollTop > 50 && !this.isScrolled) {
      header.classList.add('scrolled');
      this.isScrolled = true;
    } else if (scrollTop <= 50 && this.isScrolled) {
      header.classList.remove('scrolled');
      this.isScrolled = false;
    }
  }

       async handleRouteChange() {
    const hash = window.location.hash.slice(1) || '/';
    console.log('📍 Маршрут изменен на:', hash, 'Предыдущий hash:', this.lastHandledHash);
    
    // Защита от повторного вызова (но разрешаем, если hash действительно изменился)
    const previousHash = this.lastHandledHash || '';
    if (this.isHandlingRoute && previousHash === hash) {
      console.log('⚠️ handleRouteChange уже выполняется для того же hash, пропускаем');
      return;
    }
    
    // Если hash не изменился, но мы не обрабатываем, всё равно обрабатываем (для принудительного вызова)
    if (previousHash === hash && !this.isHandlingRoute) {
      console.log('🔄 Hash не изменился, но принудительно обрабатываем');
    }
    
    this.isHandlingRoute = true;
    this.lastHandledHash = hash;
    
    try {
      if (hash === '/' || hash === '') {
        console.log('🏠 Загружаем главную страницу');
        await this.loadHomePage();
      } else if (hash.startsWith('/simulation/')) {
        const simulationId = hash.split('/')[2];
        console.log('⚡ Загружаем симуляцию:', simulationId);
        await this.loadSimulationPage(simulationId);
      } else if (hash.startsWith('/player/')) {
        const simulationId = hash.split('/')[2];
        console.log('➡️ Плеер отключён, переходим на страницу симуляции:', simulationId);
        window.location.hash = `/simulation/${simulationId}`;
        this.isHandlingRoute = false;
        return;
      } else if (hash === '/about') {
        console.log('ℹ️ Загружаем страницу "О проекте"');
        await this.loadAboutPage();
      } else if (hash === '/demo') {
        console.log('🎬 Загружаем страницу демо');
        await this.loadDemoPage();
      } else if (hash === '/contact') {
        console.log('📞 Загружаем страницу контактов');
        await this.loadContactPage();
      } else if (hash === '/auth') {
        console.log('🔐 Загружаем страницу авторизации');
        await this.loadAuthPage();
      } else if (hash === '/teacher-dashboard') {
        console.log('👨‍🏫 Загружаем кабинет учителя');
        await this.loadTeacherDashboardPage();
      } else {
        console.log('🏠 Загружаем главную страницу (по умолчанию)');
        await this.loadHomePage();
      }
      console.log('✅ Страница загружена успешно');
      this.updateAuthButton();
    } catch (error) {
      console.error('❌ Ошибка при загрузке страницы:', error);
    } finally {
      // Сбрасываем флаг после завершения
      setTimeout(() => {
        this.isHandlingRoute = false;
      }, 100);
    }
  }

  /**
   * Обновить кнопку авторизации в header
   */
  async updateAuthButton() {
    try {
      const { default: auth } = await import('./core/auth.js');
      const authButtons = document.getElementById('auth-buttons');
      if (!authButtons) return;

      if (auth.isAuthenticated()) {
        const user = auth.getCurrentUser();
        authButtons.innerHTML = `
          <button class="btn btn-secondary" onclick="if(window.PhysicsLabApp) { window.PhysicsLabApp.logout(); }">
            Выйти (${user.name})
          </button>
          ${auth.isTeacher() ? `
            <button class="btn btn-primary ml-2" onclick="if(window.PhysicsLabApp) { window.PhysicsLabApp.navigateTo('teacher-dashboard'); } else { window.location.hash = '/teacher-dashboard'; window.dispatchEvent(new Event('hashchange')); }">
              Кабинет
            </button>
          ` : ''}
        `;
      } else {
        authButtons.innerHTML = `
          <button class="btn btn-primary" onclick="if(window.PhysicsLabApp) { window.PhysicsLabApp.navigateTo('auth'); } else { window.location.hash = '/auth'; window.dispatchEvent(new Event('hashchange')); }">
            Войти
          </button>
        `;
      }
    } catch (error) {
      console.error('Ошибка обновления кнопки авторизации:', error);
    }
  }

  /**
   * Выход из системы
   */
  async logout() {
    try {
      const { default: auth } = await import('./core/auth.js');
      auth.logout();
      this.updateAuthButton();
      window.location.hash = '/';
    } catch (error) {
      console.error('Ошибка выхода:', error);
    }
  }


  async loadHomePage() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Временно отключаем плавную прокрутку и мгновенно переходим вверх
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Прокручиваем страницу вверх при загрузке главной страницы (без анимации)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Для Safari
    
    // Восстанавливаем плавную прокрутку
    html.style.scrollBehavior = originalScrollBehavior;

    // Остановить текущую симуляцию при выходе на главную
    if (this.currentSimulation && typeof this.currentSimulation.destroy === 'function') {
      try {
        this.currentSimulation.destroy();
      } catch (e) {
        console.warn('Не удалось корректно уничтожить симуляцию при выходе на главную:', e);
      }
      this.currentSimulation = null;
    }

    // Используем fallback версию, которая работает корректно со стилями
    this.loadHomePageFallback();
    
    // Применяем переводы к загруженному контенту с небольшой задержкой
    if (this.i18n) {
      setTimeout(() => {
        this.updatePageTranslations();
      }, 100);
    }
  }

  loadHomePageFallback() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Принудительно применяем белый цвет к заголовкам в темной теме
    this.forceWhiteHeaders();

    mainContent.innerHTML = `
      <!-- Breadcrumbs Navigation -->
      <nav class="breadcrumbs" aria-label="Навигация по сайту">
        <div class="container">
          <ol class="breadcrumb-list">
            <li class="breadcrumb-item">
              <a href="/" class="breadcrumb-link" data-i18n="nav.home">Главная</a>
            </li>
            <li class="breadcrumb-item active" aria-current="page">
              <span data-i18n="nav.simulations">Симуляции</span>
            </li>
          </ol>
        </div>
      </nav>

      <!-- Premium Hero Section -->
      <section class="hero">
        <div class="container">
          <div class="hero-content">
            <h1 class="hero-title dark:text-white" data-i18n="premium.platform.subtitle">Изучайте физику<br>по-новому</h1>
                         <p class="hero-subtitle" data-i18n="hero.subtitle">
               Откройте для себя мир физики через интерактивные симуляции и современные технологии обучения.
             </p>
                         <div class="hero-actions">
               <button class="btn btn-primary btn-lg" onclick="window.PhysicsLabApp.navigateTo('simulation/ohms-law')" data-i18n="nav.start.learning">
                 <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                 </svg>
                 <span data-i18n="nav.start.learning">Начать изучение</span>
               </button>
               <button class="btn btn-secondary btn-lg" onclick="window.PhysicsLabApp.navigateTo('demo')" data-i18n="nav.learn.more">
                 <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                 </svg>
                 <span data-i18n="nav.learn.more">Смотреть демо</span>
               </button>
             </div>
          </div>
        </div>
      </section>

      <!-- Premium Features Section -->
      <section class="features">
        <div class="container">
                     <div class="section-header">
             <h2 class="section-title dark:text-white" data-i18n="features.title">Возможности платформы</h2>
             <p class="section-subtitle" data-i18n="features.subtitle">
               Инновационные инструменты и технологии для глубокого изучения физики с современным пользовательским опытом.
             </p>
           </div>

          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <img src="/images/thumbs/Interactive_simulations.webp" alt="Интерактивные симуляции" class="feature-image">
              </div>
                             <h3 class="feature-title" data-i18n="features.simulations.title">Интерактивные симуляции</h3>
               <p class="feature-description" data-i18n="features.simulations.desc">
                 Погрузитесь в мир физики через высококачественные интерактивные модели с продвинутой графикой и анимацией.
               </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <img src="/images/thumbs/Precise_measurements.webp" alt="Точные измерения" class="feature-image">
              </div>
                             <h3 class="feature-title" data-i18n="features.measurements.title">Точные измерения</h3>
               <p class="feature-description" data-i18n="features.measurements.desc">
                 Получайте прецизионные данные и профессиональные графики в реальном времени для глубокого анализа экспериментов.
               </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <img src="/images/thumbs/Modern_interface.webp" alt="Современный интерфейс" class="feature-image">
              </div>
                             <h3 class="feature-title" data-i18n="features.interface.title">Современный интерфейс</h3>
               <p class="feature-description" data-i18n="features.interface.desc">
                 Наслаждайтесь интуитивным и элегантным интерфейсом на любом устройстве для комфортного изучения.
               </p>
            </div>

                                   <div class="feature-card">
                         <div class="feature-icon">
                           <img src="/images/thumbs/Gamification.webp" alt="Геймификация обучения" class="feature-image">
                         </div>
                         <h3 class="feature-title" data-i18n="features.analytics.title">Геймификация обучения</h3>
                         <p class="feature-description" data-i18n="features.analytics.desc">
                           Учитесь физике в формате увлекательных игр и квестов, зарабатывайте достижения и соревнуйтесь с друзьями.
                         </p>
                       </div>
          </div>
        </div>
      </section>

      <!-- Premium Simulations Section -->
             <section id="simulations-showcase" class="simulations">
        <div class="container">
                     <div class="section-header">
             <h2 class="section-title dark:text-white" data-i18n="home.simulations.title">Доступные симуляции</h2>
             <p class="section-subtitle" data-i18n="home.simulations.subtitle">
               Исследуйте физику через интерактивные симуляции и эксперименты.
             </p>
           </div>

          <div class="simulations-grid">
                         <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/ohms-law')">
                          <div class="simulation-preview">
              <div class="simulation-icon"><img src="/images/thumbs/Electric_circuits.webp" alt="Electric circuits" loading="eager" decoding="sync" fetchpriority="high" /></div>
            </div>
              <div class="simulation-content">
                                                                   <h3 class="simulation-title" data-i18n="sim.ohms.title">Закон Ома - Изучаем электричество</h3>
                  <p class="simulation-description" data-i18n="sim.ohms.description">
                    Создавайте электрические цепи и изучайте закон Ома! Перетаскивайте элементы, соединяйте их проводами 
                    и наблюдайте, как меняется ток и напряжение. Отлично подходит для школьников 7-11 классов.
                  </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.electricity">Электричество</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                    </svg>
                     <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>

                         <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/projectile-motion')">
                          <div class="simulation-preview">
              <div class="simulation-icon"><img src="/images/thumbs/Projectile.webp" alt="Projectile motion" loading="eager" decoding="sync" fetchpriority="high" /></div>
            </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.projectile.title">Движение снаряда</h3>
                <p class="simulation-description" data-i18n="sim.projectile.description">
                  Исследуйте траектории движения тел под действием силы тяжести и других сил 
                  в реалистичной 3D-среде.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.mechanics">Механика</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                    </svg>
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>

                         <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/pendulum')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Pendulm.webp" alt="Pendulum" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.pendulum.title">Маятник</h3>
                <p class="simulation-description" data-i18n="sim.pendulum.description">
                  Изучите колебательные движения и гармонические колебания в различных условиях 
                  с детальным анализом параметров.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.oscillations">Колебания</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                    </svg>
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>
            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/diffusion')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Diffusion.webp" alt="Diffusion" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.diffusion.title">Диффузия</h3>
                <p class="simulation-description" data-i18n="sim.diffusion.description">
                  Наблюдайте, как частицы двух газов смешиваются после открытия перегородки. Меняйте температуру и количество частиц.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.molecular">Молекулярная физика</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                    </svg>
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>

            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/gravity-and-orbits')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Gravity_orbits.webp" alt="Gravity and orbits" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.gravity.title">Гравитация и орбиты</h3>
                <p class="simulation-description" data-i18n="sim.gravity.description">
                  Исследуйте орбиты Солнца–Земли–Луны, скорость времени и следы траекторий.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.astronomy">Астрономия</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>

            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/geometric-optics')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Geomptics.webp" alt="Geometric optics" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.optics.title">Геометрическая оптика</h3>
                <p class="simulation-description" data-i18n="sim.optics.description">
                  Изучайте линзы, зеркала и лучи света с интерактивными схемами.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.optics">Оптика</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>

            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/charges-and-fields')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/Charge_and_Fields.png" alt="Charges and fields" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.charges.title">Заряды и поля</h3>
                <p class="simulation-description" data-i18n="sim.charges.description">
                  Исследуйте электрические поля, размещайте заряды и изучайте их взаимодействие.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.electricity">Электричество</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>
            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/interference-waves')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Interference.webp" alt="Interference and waves" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.waves.title">Интерференция и волны</h3>
                <p class="simulation-description" data-i18n="sim.waves.description">
                  Двухисточник, стоячие волны и круговые волны с наглядной интерференцией.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.waves">Волны</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>
            
            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/build-nucleus')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Nucleos.webp" alt="Build nucleus" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.nucleus.title">Сборка ядра</h3>
                <p class="simulation-description" data-i18n="sim.nucleus.description">
                  Создавайте атомные ядра, добавляя протоны и нейтроны. Изучайте ядерные силы и стабильность элементов.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.modern">Современная физика</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>
            
            <div class="simulation-card" onclick="window.PhysicsLabApp.navigateTo('simulation/higgs-boson')">
              <div class="simulation-preview">
                <div class="simulation-icon"><img src="/images/thumbs/Higgs.webp" alt="Higgs boson" loading="eager" decoding="sync" fetchpriority="high" /></div>
              </div>
              <div class="simulation-content">
                <h3 class="simulation-title" data-i18n="sim.higgs.title">Бозон Хиггса</h3>
                <p class="simulation-description" data-i18n="sim.higgs.description">
                  Как частицы получают массу: движение через поле Хиггса с наглядной визуализацией.
                </p>
                <div class="simulation-meta">
                  <span class="simulation-category" data-i18n="category.modern">Современная физика</span>
                  <button class="btn btn-primary btn-sm simulation-button" data-i18n="action.start">
                    <span data-i18n="action.start"></span>
                  </button>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      <!-- FAQ Section with Schema.org -->
      <section class="faq-section">
        <div class="container">
          <h2 class="section-title dark:text-white" data-i18n="faq.title">Часто задаваемые вопросы</h2>
          <div class="faq-list" itemScope itemType="https://schema.org/FAQPage">
            
            <div class="faq-item" itemScope itemprop="mainEntity" itemType="https://schema.org/Question">
              <h3 class="faq-question" itemprop="name" data-i18n="faq.what.is">Что такое Илим Lab?</h3>
              <div class="faq-answer" itemScope itemprop="acceptedAnswer" itemType="https://schema.org/Answer">
                <div itemprop="text">
                  <p data-i18n="faq.what.is.answer">Илим Lab — это интерактивная образовательная платформа для изучения физики через симуляции. Мы предоставляем инструменты для школьников и студентов, которые помогают понять сложные физические концепции через практические эксперименты.</p>
                </div>
              </div>
            </div>

            <div class="faq-item" itemScope itemprop="mainEntity" itemType="https://schema.org/Question">
              <h3 class="faq-question" itemprop="name" data-i18n="faq.languages">На каких языках доступна платформа?</h3>
              <div class="faq-answer" itemScope itemprop="acceptedAnswer" itemType="https://schema.org/Answer">
                <div itemprop="text">
                  <p data-i18n="faq.languages.answer">Илим Lab поддерживает три языка: русский, английский и кыргызский. Вы можете переключаться между языками в любое время.</p>
                </div>
              </div>
            </div>

            <div class="faq-item" itemScope itemprop="mainEntity" itemType="https://schema.org/Question">
              <h3 class="faq-question" itemprop="name" data-i18n="faq.teachers">Подходит ли платформа для учителей?</h3>
              <div class="faq-answer" itemScope itemprop="acceptedAnswer" itemType="https://schema.org/Answer">
                <div itemprop="text">
                  <p data-i18n="faq.teachers.answer">Да, Илим Lab отлично подходит для использования в образовательных учреждениях. Учителя могут использовать симуляции для демонстрации физических явлений и создания интерактивных уроков.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    `;

    // Изображения симуляций и возможностей платформы теперь работают корректно

    // Re-initialize scroll effects for new content
    setTimeout(() => this.setupScrollEffects(), 100);
    
    // Применяем переводы к загруженному контенту с небольшой задержкой
    if (this.i18n) {
      setTimeout(() => {
        this.updatePageTranslations();
      }, 100);
    }
  }

  async loadSimulationPage(simulationId) {
    console.log('🚀 loadSimulationPage вызвана с ID:', simulationId);
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
      console.error('❌ mainContent не найден');
      return;
    }
    console.log('✅ mainContent найден');

    // Изображения симуляций и возможностей платформы теперь работают корректно

    // Глобальный отступ под фиксированным верхним оверлеем для всех страниц симуляций
    // Используем инлайн-стиль, чтобы гарантированно перебить стили и tailwind-классы
    mainContent.style.paddingTop = '70px';

    // Временно отключаем плавную прокрутку и мгновенно переходим вверх
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Прокручиваем страницу вверх при загрузке симуляции (без анимации)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Для Safari
    
    // Восстанавливаем плавную прокрутку
    html.style.scrollBehavior = originalScrollBehavior;

    // Останавливаем и очищаем предыдущую симуляцию при переходе
    if (this.currentSimulation && typeof this.currentSimulation.destroy === 'function') {
      try {
        this.currentSimulation.destroy();
      } catch (e) {
        console.warn('Не удалось корректно уничтожить предыдущую симуляцию:', e);
      }
      this.currentSimulation = null;
    }

    // Exit fullscreen mode if active
    if (this.isFullscreenMode) {
      this.exitFullscreenMode();
    }

    const simulationNames = {
      'ohms-law': 'Закон Ома - Интерактивная сборка цепей',
      'projectile-motion': 'Движение снаряда',
      'pendulum': 'Маятник',
      'diffusion': 'Диффузия',
      'higgs-boson': 'Бозон Хиггса: Как частицы получают массу',
      'gravity-and-orbits': 'Гравитация и орбиты',
      'geometric-optics': 'Геометрическая оптика',
      'interference-waves': 'Интерференция и волны',
      'build-nucleus': 'Сборка ядра'
    };

    const simulationName = simulationNames[simulationId] || 'Симуляция';

    // Специальная обработка для симуляции закона Ома
    if (simulationId === 'ohms-law') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10"> 
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.ohms.title">Закон Ома - Изучаем электричество</h1>
              <p class="text-secondary" data-i18n="sim.ohms.detailed">Создавайте электрические цепи, перетаскивая элементы и изучайте закон Ома</p>
            </div>
            
            <div class="glass-card p-6">
              <div class="mb-6">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                                     <div class="flex gap-2">
                     <button id="example-circuit" class="btn btn-success">
                       <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                       </svg>
                       <span data-i18n="simulation.example.circuit">Пример цепи</span>
                     </button>
                     <button id="start-simulation" class="btn btn-primary">
                       <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                       </svg>
                       <span data-i18n="simulation.start"></span>
                     </button>
                     <button id="reset-simulation" class="btn btn-secondary">
                       <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                       </svg>
                       <span data-i18n="simulation.reset">Сброс</span>
                     </button>
                     <button id="lab-work-btn-ohms-law" class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('ohms-law')">
                       <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                       </svg>
                       <span>Лабораторная работа</span>
                     </button>
                   </div>
                </div>
                
                

                <!-- Контейнер канваса (скрытый до инлайнового запуска) -->
                <div id="canvas-container" class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <canvas id="simulation-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
                </div>
                
                                                                   <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 simulation-instructions" style="color: inherit;">
                    <h4 class="font-bold text-blue-800 dark:text-blue-200 mb-2" data-i18n="simulation.instructions.title">🎓 Как работать с симуляцией:</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-white" style="color: inherit;">
                      <div>
                        <h5 class="font-semibold mb-1 text-blue-800 dark:text-blue-200" data-i18n="simulation.instructions.circuit.title">🔧 Создание цепи:</h5>
                        <ul class="list-disc list-inside space-y-1" style="color: inherit;">
                          <li data-i18n="simulation.instructions.circuit.drag" style="color: inherit;"><strong>Перетащите</strong> элементы из панели справа на поле</li>
                          <li data-i18n="simulation.instructions.circuit.click1" style="color: inherit;"><strong>Кликните на контакт</strong> (маленький кружочек) первого элемента</li>
                          <li data-i18n="simulation.instructions.circuit.click2" style="color: inherit;"><strong>Кликните на контакт</strong> второго элемента → появится провод!</li>
                          <li data-i18n="simulation.instructions.circuit.connect" style="color: inherit;">Соедините <strong>батарею</strong> с <strong>резистором</strong></li>
                          <li data-i18n="simulation.instructions.circuit.add" style="color: inherit;">Добавьте <strong>лампочку</strong> и <strong>приборы</strong></li>
                        </ul>
                      </div>
                      <div>
                        <h5 class="font-semibold mb-1 text-blue-800 dark:text-blue-200" data-i18n="simulation.instructions.experiments.title">⚙️ Эксперименты:</h5>
                        <ul class="list-disc list-inside space-y-1" style="color: inherit;">
                          <li data-i18n="simulation.instructions.experiments.battery" style="color: inherit;"><strong>Клик +/-</strong> на батарее → изменить напряжение</li>
                          <li data-i18n="simulation.instructions.experiments.resistor" style="color: inherit;"><strong>Клик +/-</strong> на резисторе → изменить сопротивление</li>
                          <li data-i18n="simulation.instructions.experiments.switch" style="color: inherit;"><strong>Клик</strong> на выключатель → включить/выключить</li>
                          <li data-i18n="simulation.instructions.experiments.delete" style="color: inherit;"><strong>Delete</strong> → удалить элемент</li>
                          <li data-i18n="simulation.instructions.experiments.drag" style="color: inherit;"><strong>Перетаскивайте</strong> элементы мышкой</li>
                        </ul>
                      </div>
                    </div>
                    <div class="mt-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                      <p class="text-green-800 dark:text-white text-xs" style="color: inherit;"><strong data-i18n="simulation.instructions.task.title">🎯 Задание:</strong> <span data-i18n="simulation.instructions.task.text">Создайте цепь: батарея → резистор → лампочка. Измените сопротивление и посмотрите, как меняется яркость лампочки!</span></p>
                    </div>
                    <div class="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
                      <p class="text-yellow-800 dark:text-white text-xs" style="color: inherit;"><strong data-i18n="simulation.instructions.hint.title">💡 Подсказка:</strong> <span data-i18n="simulation.instructions.hint.text">Нажмите "Пример цепи" для быстрого старта. Затем экспериментируйте с параметрами!</span></p>
                    </div>
                  </div>
              </div>
              
                             <div class="flex justify-between items-center">
                 <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                   <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                   </svg>
                   <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
                 </button>
               </div>
            </div>
          </div>
        </div>
      `;

      // Не инициализируем сразу: ожидаем действие пользователя (клик по постеру)
      const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const launchInlineBtn = document.getElementById('launch-inline');
      const poster = document.getElementById('player-poster');
      const posterPlay = document.getElementById('poster-play');
      const canvasContainer = document.getElementById('canvas-container');

      const openPlayer = () => {
        // Плеер отключён, запускаем сразу на странице
        openInline();
      };

      const openInline = () => {
        if (poster) poster.classList.add('hidden');
        if (canvasContainer) canvasContainer.classList.remove('hidden');
        // Даем DOM применить классы, затем инициализируем
        setTimeout(() => this.initOhmsLawSimulation(), 50);
      };

      // Запускаем сразу на странице без плеера и постера
      openInline();

      // кнопки плеера удалены
      if (launchInlineBtn) launchInlineBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openInline(); });
      if (poster) {
        poster.addEventListener('click', (e) => { e.preventDefault(); openInline(); });
        poster.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openInline(); } });
      }
      if (posterPlay) {
        posterPlay.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openInline(); });
        posterPlay.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openInline(); } });
      }
    } else if (simulationId === 'projectile-motion') {
      console.log('🎯 Загружаем страницу projectile-motion');
      // Страница для "Движение снаряда"
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-44 pb-12">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.projectile.title">Движение снаряда</h1>
              <p class="text-secondary" data-i18n="sim.projectile.detailed">Исследуйте баллистическое движение: настройте скорость, угол и высоту запуска</p>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div class="lg:col-span-2 glass-card p-6">
                <div class="mb-4 flex items-center justify-between">
                  <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                  <div class="flex gap-2">
                    <button id="start-projectile" class="btn btn-primary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span data-i18n="simulation.start"></span>
                    </button>
                    <button id="reset-projectile" class="btn btn-secondary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <span data-i18n="simulation.reset">Сброс</span>
                    </button>
                    <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('projectile-motion')">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                      </svg>
                      <span>Лабораторная работа</span>
                    </button>
                  </div>
                </div>

                <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-6">
                  <canvas id="projectile-canvas" width="1200" height="500" style="display: block; width: 100%; height: 500px;"></canvas>
                </div>
              </div>

              <div class="glass-card p-6">
                <h3 class="text-xl font-bold text-primary mb-4" data-i18n="projectile.parameters.title">Параметры</h3>

                <div class="space-y-5">
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="projectile.parameters.initial.velocity">Начальная скорость (m/s)</label>
                    <div class="flex items-center gap-3">
                      <input id="vel-range" type="range" min="5" max="50" step="1" value="20" class="w-full" />
                      <input id="vel-number" type="number" min="5" max="50" step="1" value="20" class="w-20 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="projectile.parameters.angle">Угол запуска (°)</label>
                    <div class="flex items-center gap-3">
                      <input id="angle-range" type="range" min="0" max="90" step="1" value="45" class="w-full" />
                      <input id="angle-number" type="number" min="0" max="90" step="1" value="45" class="w-20 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="projectile.parameters.height">Высота запуска (m)</label>
                    <div class="flex items-center gap-3">
                      <input id="height-range" type="range" min="0" max="100" step="1" value="5" class="w-full" />
                      <input id="height-number" type="number" min="0" max="100" step="1" value="5" class="w-20 input" />
                    </div>
                  </div>

                  <div class="p-3 bg-blue-50 rounded-lg border border-blue-200 text-blue-800 text-sm" data-i18n="projectile.parameters.tip">
                    Совет: изменяйте параметры и наблюдайте, как меняется траектория, дальность и время полета.
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>
      `;

      console.log('✅ HTML для projectile-motion загружен в DOM');
      
      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initProjectileMotionSimulation();
      }, 200);
    } else if (simulationId === 'build-nucleus') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.nucleus.title">Сборка ядра</h1>
              <p class="text-secondary" data-i18n="sim.nucleus.description">Создавайте атомные ядра, добавляя протоны и нейтроны. Изучайте ядерные силы и стабильность элементов.</p>
            </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                  <button id="nucleus-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('build-nucleus')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
              </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-6 relative">
                <canvas id="nucleus-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
                
                <!-- Overlay controls -->
                <div class="absolute top-4 left-4 flex flex-col gap-3">
                  <!-- Protons Control -->
                  <div class="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-gray-200/50 hover:shadow-2xl transition-all duration-300">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-2">
                        <div class="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-green-600 shadow-inner"></div>
                        <h4 class="text-sm font-bold text-gray-800" data-i18n="nucleus.protons">Протоны:</h4>
                      </div>
                      <div class="text-xs text-gray-500 font-medium" data-i18n="nucleus.charge">+1e</div>
                    </div>
                    <div class="flex items-center justify-between">
                      <button id="remove-proton" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 hover:border-green-300 hover:from-green-100 hover:to-green-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95">
                        <span class="absolute inset-0 flex items-center justify-center text-green-600 font-bold text-lg group-hover:text-green-700 transition-colors">−</span>
                      </button>
                      <div class="flex flex-col items-center">
                        <span class="text-2xl font-bold text-green-700" id="proton-count">1</span>
                        <div class="text-xs text-gray-500 font-medium" data-i18n="nucleus.particles">частиц</div>
                      </div>
                      <button id="add-proton" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 hover:border-green-300 hover:from-green-100 hover:to-green-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95">
                        <span class="absolute inset-0 flex items-center justify-center text-green-600 font-bold text-lg group-hover:text-green-700 transition-colors">+</span>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Pair Control -->
                  <div class="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-gray-200/50 hover:shadow-2xl transition-all duration-300">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-2">
                        <div class="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 shadow-inner"></div>
                        <h4 class="text-sm font-bold text-gray-800" data-i18n="nucleus.pair.tooltip">Протон + Нейтрон</h4>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <button id="remove-pair" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 hover:border-blue-300 hover:from-blue-100 hover:to-blue-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95" title="Убрать пару (протон + нейтрон)">
                        <span class="absolute inset-0 flex items-center justify-center text-blue-600 font-bold text-lg group-hover:text-blue-700 transition-colors">−</span>
                      </button>
                      <div class="flex flex-col items-center">
                        <span class="text-xs text-gray-500 font-medium" data-i18n="nucleus.add.pair">Добавить пару</span>
                      </div>
                      <button id="add-pair" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 hover:border-blue-300 hover:from-blue-100 hover:to-blue-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95" title="Добавить пару (протон + нейтрон)">
                        <span class="absolute inset-0 flex items-center justify-center text-blue-600 font-bold text-lg group-hover:text-blue-700 transition-colors">+</span>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Neutrons Control -->
                  <div class="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-gray-200/50 hover:shadow-2xl transition-all duration-300">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-2">
                        <div class="w-3 h-3 rounded-full bg-gradient-to-r from-pink-400 to-pink-600 shadow-inner"></div>
                        <h4 class="text-sm font-bold text-gray-800" data-i18n="nucleus.neutrons">Нейтроны:</h4>
                      </div>
                      <div class="text-xs text-gray-500 font-medium" data-i18n="nucleus.neutral">0e</div>
                    </div>
                    <div class="flex items-center justify-between">
                      <button id="remove-neutron" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-pink-50 to-pink-100 border-2 border-pink-200 hover:border-pink-300 hover:from-pink-50 hover:to-pink-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95">
                        <span class="absolute inset-0 flex items-center justify-center text-pink-600 font-bold text-lg group-hover:text-pink-700 transition-colors">−</span>
                      </button>
                      <div class="flex flex-col items-center">
                        <span class="text-2xl font-bold text-pink-700" id="neutron-count">1</span>
                        <div class="text-xs text-gray-500 font-medium" data-i18n="nucleus.particles">частиц</div>
                      </div>
                      <button id="add-neutron" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-pink-50 to-pink-100 border-2 border-pink-200 hover:border-pink-300 hover:from-pink-50 hover:to-pink-200 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95">
                        <span class="absolute inset-0 flex items-center justify-center text-pink-600 font-bold text-lg group-hover:text-pink-700 transition-colors">+</span>
                      </button>
                    </div>
                  </div>
                  
                  <div class="bg-white bg-opacity-90 rounded-lg p-3 shadow-lg">
                    <label class="flex items-center gap-2 text-sm">
                      <input type="checkbox" id="toggle-electron-cloud" checked class="rounded">
                      <span class="text-gray-700" data-i18n="nucleus.electron.cloud">Электронное облако</span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="pt-2">
                <h3 class="text-xl font-bold text-primary mb-4" data-i18n="nucleus.instructions">Инструкции</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600" id="nucleus-instructions">
                  <div>
                    <h5 class="font-semibold mb-2" data-i18n="nucleus.how.to.use">Как использовать:</h5>
                    <ul class="list-disc list-inside space-y-1">
                      <li data-i18n="nucleus.click.nucleus">Кликните в области ядра для добавления нуклонов</li>
                      <li data-i18n="nucleus.protons.auto">Протоны добавляются автоматически для баланса</li>
                      <li data-i18n="nucleus.drag.nucleons">Перетаскивайте нуклоны для перемещения</li>
                      <li data-i18n="nucleus.observe.forces">Наблюдайте за ядерными силами и стабильностью</li>
                    </ul>
                  </div>
                  <div>
                    <h5 class="font-semibold mb-2" data-i18n="nucleus.physics">Физика:</h5>
                    <ul class="list-disc list-inside space-y-1">
                      <li data-i18n="nucleus.green.circles">Зеленые круги - протоны (P)</li>
                      <li data-i18n="nucleus.pink.circles">Розовые круги - нейтроны (N)</li>
                      <li data-i18n="nucleus.nuclear.forces">Ядерные силы удерживают нуклоны вместе</li>
                      <li data-i18n="nucleus.coulomb.repulsion">Кулоновское отталкивание между протонами</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initBuildNucleusSimulation();
      }, 100);
    } else if (simulationId === 'pendulum') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.pendulum.title">Маятник</h1>
              <p class="text-secondary" data-i18n="sim.pendulum.description">Математический маятник: исследуйте влияние длины, затухания и гравитации на период</p>
            </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                  <button id="pendulum-start" class="btn btn-primary">
                    <span data-i18n="simulation.start"></span>
                  </button>
                  <button id="pendulum-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('pendulum')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
              </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-6">
                <canvas id="pendulum-canvas" width="1200" height="500" style="display: block; width: 100%; height: 500px;"></canvas>
              </div>

              <div class="pt-2">
                <h3 class="text-xl font-bold text-primary mb-4" data-i18n="pendulum.parameters.title">Параметры</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="pendulum.parameters.length">Длина (m)</label>
                    <div class="flex items-center gap-3">
                      <input id="pend-length-range" type="range" min="0.1" max="5" step="0.1" value="1" class="w-full" />
                      <input id="pend-length-number" type="number" min="0.1" max="5" step="0.1" value="1" class="w-24 input" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="pendulum.parameters.mass">Масса (kg)</label>
                    <div class="flex items-center gap-3">
                      <input id="pend-mass-range" type="range" min="0.1" max="10" step="0.1" value="1" class="w-full" />
                      <input id="pend-mass-number" type="number" min="0.1" max="10" step="0.1" value="1" class="w-24 input" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="pendulum.parameters.amplitude">Амплитуда (°)</label>
                    <div class="flex items-center gap-3">
                      <input id="pend-amp-range" type="range" min="1" max="89" step="1" value="30" class="w-full" />
                      <input id="pend-amp-number" type="number" min="1" max="89" step="1" value="30" class="w-24 input" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="pendulum.parameters.damping">Затухание β</label>
                    <div class="flex items-center gap-3">
                      <input id="pend-damp-range" type="range" min="0" max="0.2" step="0.005" value="0" class="w-full" />
                      <input id="pend-damp-number" type="number" min="0" max="0.2" step="0.005" value="0" class="w-24 input" />
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="pendulum.parameters.gravity">g (m/s²)</label>
                    <div class="flex items-center gap-3">
                      <input id="pend-g-range" type="range" min="0.5" max="20" step="0.1" value="9.81" class="w-full" />
                      <input id="pend-g-number" type="number" min="0.5" max="20" step="0.1" value="9.81" class="w-24 input" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initPendulumSimulation();
      }, 100);
    } else if (simulationId === 'diffusion') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.diffusion.title">Диффузия</h1>
              <p class="text-secondary" data-i18n="sim.diffusion.description">Два газа, разделённые перегородкой, начинают смешиваться после её открытия</p>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div class="lg:col-span-2 glass-card p-6">
                <div class="mb-4 flex items-center justify-between">
                  <h3 class="text-xl font-bold text-primary" data-i18n="sim.diffusion.interactive">Интерактивная симуляция</h3>
                  <div class="flex gap-2">
                    <button id="diffusion-start" class="btn btn-primary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span data-i18n="simulation.start"></span>
                    </button>
                    <button id="diffusion-reset" class="btn btn-secondary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <span data-i18n="simulation.reset">Сброс</span>
                    </button>
                    <button id="diffusion-toggle-barrier" class="btn btn-secondary" data-i18n="sim.diffusion.open.partition">Открыть перегородку</button>
                    <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('diffusion')">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                      </svg>
                      <span>Лабораторная работа</span>
                    </button>
                  </div>
                </div>

                <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-6">
                  <canvas id="diffusion-canvas" width="1200" height="520" style="display: block; width: 100%; height: 520px;"></canvas>
                </div>
              </div>

              <div class="glass-card p-6">
                <h3 class="text-xl font-bold text-primary mb-4" data-i18n="sim.diffusion.parameters">Параметры</h3>
                <div class="space-y-5">
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.particles.a">Частицы A (синие)</label>
                    <div class="flex items-center gap-3">
                      <input id="numA-range" type="range" min="0" max="400" step="10" value="150" class="w-full" />
                      <input id="numA-number" type="number" min="0" max="400" step="10" value="150" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.particles.b">Частицы B (красные)</label>
                    <div class="flex items-center gap-3">
                      <input id="numB-range" type="range" min="0" max="400" step="10" value="150" class="w-full" />
                      <input id="numB-number" type="number" min="0" max="400" step="10" value="150" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.temperature.a">Начальная температура A (K)</label>
                    <div class="flex items-center gap-3">
                      <input id="tempA-range" type="range" min="200" max="900" step="25" value="300" class="w-full" />
                      <input id="tempA-number" type="number" min="200" max="900" step="25" value="300" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.temperature.b">Начальная температура B (K)</label>
                    <div class="flex items-center gap="3">
                      <input id="tempB-range" type="range" min="200" max="900" step="25" value="300" class="w-full" />
                      <input id="tempB-number" type="number" min="200" max="900" step="25" value="300" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.mass.a">Масса частиц A</label>
                    <div class="flex items-center gap-3">
                      <input id="massA-range" type="range" min="0.2" max="5" step="0.1" value="1" class="w-full" />
                      <input id="massA-number" type="number" min="0.2" max="5" step="0.1" value="1" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.mass.b">Масса частиц B</label>
                    <div class="flex items-center gap-3">
                      <input id="massB-range" type="range" min="0.2" max="5" step="0.1" value="1" class="w-full" />
                      <input id="massB-number" type="number" min="0.2" max="5" step="0.1" value="1" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.radius.a">Радиус частиц A (px)</label>
                    <div class="flex items-center gap-3">
                      <input id="radiusA-range" type="range" min="2" max="20" step="1" value="4" class="w-full" />
                      <input id="radiusA-number" type="number" min="2" max="20" step="1" value="4" class="w-24 input" />
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="sim.diffusion.radius.b">Радиус частиц B (px)</label>
                    <div class="flex items-center gap-3">
                      <input id="radiusB-range" type="range" min="2" max="20" step="1" value="4" class="w-full" />
                      <input id="radiusB-number" type="number" min="2" max="20" step="1" value="4" class="w-24 input" />
                    </div>
                  </div>
                </div>
                <div class="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-blue-800 text-sm" data-i18n="sim.diffusion.instructions">
                  Нажмите «Открыть перегородку», чтобы начать диффузию. Температура A/B задается в Кельвинах (200K-900K) и определяет скорость частиц; после открытия перегородки температуры термализуются к тепловому равновесию.
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initDiffusionSimulation();
      }, 100);
    } else if (simulationId === 'higgs-boson') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.higgs.title">Бозон Хиггса</h1>
              <p class="text-secondary" data-i18n="sim.higgs.description">Масса рождается из взаимодействия с полем Хиггса</p>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div class="lg:col-span-2 glass-card p-6">
                <div class="mb-4 flex items-center justify-between">
                  <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                  <div class="flex gap-2">
                    <button id="higgs-start" class="btn btn-primary">
                      <span data-i18n="simulation.start"></span>
                    </button>
                    <button id="higgs-reset" class="btn btn-secondary">
                      <span data-i18n="simulation.reset">Сброс</span>
                    </button>
                    <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('higgs-boson')">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                      </svg>
                      <span>Лабораторная работа</span>
                    </button>
                  </div>
                </div>

                <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-6">
                  <canvas id="higgs-canvas" width="1200" height="500" style="display: block; width: 100%; height: 500px;"></canvas>
                </div>
              </div>

              <div class="glass-card p-6">
                <h3 class="text-xl font-bold text-primary mb-4" data-i18n="higgs.parameters.title">Параметры</h3>

                <div class="space-y-5">
                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="higgs.parameters.particle.type">Тип частицы</label>
                    <select id="particle-select" class="input w-full">
                      <option value="photon" data-i18n="higgs.particles.photon">Фотон (безмассовый)</option>
                      <option value="electron" data-i18n="higgs.particles.electron">Электрон (лёгкий)</option>
                      <option value="top-quark" data-i18n="higgs.particles.top.quark">Топ-кварк (тяжёлый)</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-secondary mb-2" data-i18n="higgs.parameters.coupling">Сила взаимодействия с полем Хиггса</label>
                    <div class="flex items-center gap-3">
                      <input id="coupling-range" type="range" min="0" max="1" step="0.01" value="0.5" class="w-full" />
                      <input id="coupling-number" type="number" min="0" max="1" step="0.01" value="0.5" class="w-24 input" />
                    </div>
                  </div>

                  <div class="flex items-center gap-2">
                    <input id="cartoon-toggle" type="checkbox" />
                    <label for="cartoon-toggle" class="text-secondary" data-i18n="higgs.parameters.cartoon.metaphor">Метафора "вечеринки"</label>
                  </div>

                  <div class="p-3 bg-blue-50 rounded-lg border border-blue-200 text-blue-800 text-sm" data-i18n="higgs.parameters.explanation">
                    Масса не «встроена» в частицы — они получают её, взаимодействуя с полем Хиггса.
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initHiggsBosonSimulation();
      }, 100);
    } else if (simulationId === 'gravity-and-orbits') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.gravity.title">Гравитация и орбиты</h1>
              <p class="text-secondary" data-i18n="sim.gravity.description">Солнечная система в миниатюре: Солнце–Земля–Луна</p>
            </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                                      <button id="orbits-start" class="btn btn-primary">
                      <span data-i18n="simulation.start"></span>
                    </button>
                  <button id="orbits-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('gravity-and-orbits')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
              </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-4">
                <canvas id="orbits-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
              </div>

            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initGravityOrbitsSimulation();
      }, 100);
    } else if (simulationId === 'geometric-optics') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.optics.title">Геометрическая оптика</h1>
              <p class="text-secondary" data-i18n="sim.optics.description">Изучайте линзы, зеркала и лучи света с интерактивными схемами</p>
            </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                  <button id="optics-start" class="btn btn-primary">
                    <span data-i18n="simulation.start"></span>
                  </button>
                  <button id="optics-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('geometric-optics')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
              </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-4">
                <canvas id="optics-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
              </div>

              <div class="simulation-instructions">
                <h5 class="text-lg font-semibold mb-3">🎮 Как использовать симуляцию:</h5>
                <ul class="space-y-2 text-sm">
                  <li><strong>🖱️ Десктоп:</strong> Используйте панель управления справа для выбора элементов и настроек</li>
                  <li><strong>📱 Мобильные:</strong> Используйте панель внизу экрана для управления</li>
                  <li><strong>🔄 Перетаскивание:</strong> Перетаскивайте объекты мышью или пальцем</li>
                  <li><strong>🎛️ Настройки:</strong> Изменяйте фокусное расстояние, количество лучей и другие параметры</li>
                  <li><strong>🔍 Эксперименты:</strong> Попробуйте разные типы линз и зеркал</li>
                </ul>
                </div>
                </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
                  </div>
                </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initGeometricOpticsSimulation();
      }, 100);
    } else if (simulationId === 'charges-and-fields') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.charges.title">Заряды и поля</h1>
              <p class="text-secondary" data-i18n="sim.charges.description">Исследуйте электрические поля, размещайте заряды и изучайте их взаимодействие</p>
                  </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                  <button id="charges-start" class="btn btn-primary">
                    <span data-i18n="simulation.start"></span>
                  </button>
                  <button id="charges-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('charges-and-fields')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
                </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-4">
                <canvas id="charges-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
              </div>

              <div class="simulation-instructions">
                <h5 class="text-lg font-semibold mb-3">🎮 Как использовать симуляцию:</h5>
                <ul class="space-y-2 text-sm">
                  <li><strong>⚡ Заряды:</strong> Выберите тип заряда в нижней панели и кликните по полю, чтобы разместить</li>
                  <li><strong>🖱️ Перетаскивание:</strong> Перемещайте заряды мышью или пальцем</li>
                  <li><strong>🗑️ Удаление:</strong> Двойной клик по заряду удаляет его</li>
                  <li><strong>🔍 Масштабирование:</strong> Колесо мыши или pinch‑жест для зума; перетаскивание фона — панорамирование</li>
                  <li><strong>➡️ Силовые линии:</strong> Показываются всегда, со стрелками направления поля от + к −</li>
                  
                </ul>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initChargesAndFieldsSimulation();
      }, 100);
    } else if (simulationId === 'interference-waves') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.waves.title">Интерференция и волны</h1>
              <p class="text-secondary" data-i18n="sim.waves.description">Две когерентные точки, стоячие волны и визуализация фаз</p>
            </div>

            <div class="glass-card p-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                <div class="flex gap-2">
                  <button id="waves-start" class="btn btn-primary">
                    <span data-i18n="simulation.start"></span>
                  </button>
                  <button id="waves-reset" class="btn btn-secondary">
                    <span data-i18n="simulation.reset">Сброс</span>
                  </button>
                  <button class="btn btn-outline" onclick="window.PhysicsLabApp.openLabWork('interference-waves')">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    <span>Лабораторная работа</span>
                  </button>
                </div>
              </div>

              <div class="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-4">
                <canvas id="waves-canvas" width="1200" height="600" style="display: block; width: 100%; height: 600px;"></canvas>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="waves.parameters.mode">Режим</label>
                  <select id="waves-mode" class="input w-full">
                    <option value="two-sources" data-i18n="waves.mode.two.sources">Два источника</option>
                    <option value="standing" data-i18n="waves.mode.standing">Стоячая волна</option>
                    <option value="single-source" data-i18n="waves.mode.single.source">Один источник</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="waves.parameters.wavelength">Длина волны λ (px)</label>
                  <div class="flex items-center gap-3">
                    <input id="waves-lambda" type="range" min="10" max="200" step="5" value="60" class="w-full" />
                    <input id="waves-lambda-num" type="number" min="10" max="200" step="5" value="60" class="w-24 input" />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="waves.parameters.frequency">Частота f (Гц)</label>
                  <div class="flex items-center gap-3">
                    <input id="waves-frequency" type="range" min="0.1" max="5" step="0.1" value="1" class="w-full" />
                    <input id="waves-frequency-num" type="number" min="0.1" max="5" step="0.1" value="1" class="w-24 input" />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="waves.parameters.amplitude">Амплитуда A</label>
                  <div class="flex items-center gap-3">
                    <input id="waves-amplitude" type="range" min="0.1" max="2" step="0.1" value="1" class="w-full" />
                    <input id="waves-amplitude-num" type="number" min="0.1" max="2" step="0.1" value="1" class="w-24 input" />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="waves.parameters.wave.speed">Скорость волны v (px/с)</label>
                  <div class="flex items-center gap-3">
                    <input id="waves-speed" type="range" min="10" max="500" step="10" value="100" class="w-full" />
                    <input id="waves-speed-num" type="number" min="10" max="500" step="10" value="100" class="w-24 input" />
                  </div>
                </div>
                <div class="space-y-2">
                  <label class="inline-flex items-center gap-2">
                    <input id="waves-show-phase" type="checkbox" checked />
                    <span class="text-secondary" data-i18n="waves.options.show.phase">Показывать фазу</span>
                  </label><br/>
                  <label class="inline-flex items-center gap-2">
                    <input id="waves-show-nodes" type="checkbox" />
                    <span class="text-secondary" data-i18n="waves.options.show.nodes">Узлы/пучности</span>
                  </label><br/>
                  <label class="inline-flex items-center gap-2">
                    <input id="waves-show-sources" type="checkbox" checked />
                    <span class="text-secondary" data-i18n="waves.options.show.sources">Показывать источники</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>`;

      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initInterferenceWavesSimulation();
      }, 100);
    } else if (simulationId === 'gravity-orbits-p5') {
      mainContent.innerHTML = `
        <div class="container">
          <div class="pt-32 pb-10">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="sim.gravity.title">Гравитация и орбиты</h1>
              <p class="text-secondary" data-i18n="sim.gravity.description">Интерактивная симуляция гравитационного взаимодействия</p>
            </div>
            
            <div class="glass-card p-6">
              <div class="mb-6">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-xl font-bold text-primary" data-i18n="simulation.interactive">Интерактивная симуляция</h3>
                  <div class="flex gap-2">
                    <button id="start-simulation" class="btn btn-primary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span data-i18n="simulation.start"></span>
                    </button>
                    <button id="pause-simulation" class="btn btn-secondary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span data-i18n="simulation.pause"></span>
                    </button>
                    <button id="reset-simulation" class="btn btn-secondary">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <span data-i18n="simulation.reset">Сброс</span>
                    </button>
                  </div>
                </div>
                
                <canvas id="simulation-canvas" width="800" height="600" class="w-full border border-gray-300 rounded-lg bg-gray-900"></canvas>
              </div>
              
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-primary mb-3" data-i18n="gravity.p5.buttons.add.bodies">Добавить тела</h4>
                <div class="flex gap-3">
                  <button id="add-sun" class="btn btn-warning">
                    <span class="text-yellow-500">☀️</span> <span data-i18n="gravity.p5.buttons.add.sun">+ Солнце</span>
                  </button>
                  <button id="add-earth" class="btn btn-info">
                    <span class="text-blue-500">🌍</span> <span data-i18n="gravity.p5.buttons.add.earth">+ Земля</span>
                  </button>
                  <button id="add-moon" class="btn btn-secondary">
                    <span class="text-gray-400">🌙</span> <span data-i18n="gravity.p5.buttons.add.moon">+ Луна</span>
                  </button>
                  <button id="clear-bodies" class="btn btn-danger">
                    🗑️ <span data-i18n="gravity.p5.buttons.clear.bodies">Очистить</span>
                  </button>
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label class="block text-sm font-medium text-secondary mb-2" data-i18n="gravity.p5.parameters.time.scale">Масштаб времени</label>
                  <div class="flex items-center gap-3">
                                      <input id="gravity-time-scale" type="range" min="0.1" max="20" step="0.5" value="1" class="w-full" />
                  <input id="gravity-time-scale-num" type="number" min="0.1" max="20" step="0.5" value="1" class="w-24 input" />
                  </div>
                </div>
                
                <div class="space-y-2">
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-show-vectors" type="checkbox" checked class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.show.vectors">Показать векторы</span>
                  </label>
                  <br>
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-show-orbits" type="checkbox" checked class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.show.orbits">Показать орбиты</span>
                  </label>
                  <br>
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-show-grid" type="checkbox" class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.show.grid">Показать сетку</span>
                  </label>
                  <br>
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-show-info" type="checkbox" checked class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.show.info">Показать информацию</span>
                  </label>
                </div>
                
                <div class="space-y-2">
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-enabled" type="checkbox" checked class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.gravity.enabled">Гравитация включена</span>
                  </label>
                  <br>
                  <label class="inline-flex items-center gap-2">
                    <input id="gravity-collisions" type="checkbox" checked class="checkbox" />
                    <span class="text-sm" data-i18n="gravity.p5.options.collisions">Столкновения</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-between items-center">
              <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
              </button>
            </div>
          </div>
        </div>
      `;
      
      // Даем время DOM полностью загрузиться перед инициализацией симуляции
      setTimeout(() => {
        this.initGravityOrbitsP5Simulation();
      }, 100);
    } else {
      // Для других симуляций показываем заглушку
      mainContent.innerHTML = `
        <div class="container">
          <div class="py-8">
            <div class="mb-8">
              <h1 class="text-3xl font-bold text-primary mb-4" data-i18n="simulation.in.development">Симуляция в разработке</h1>
              <p class="text-secondary" data-i18n="simulation.coming.soon">Интерактивная симуляция для изучения физики</p>
            </div>
            
            <div class="glass-card p-6">
              <div class="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mb-6">
                <div class="text-center">
                  <div class="w-24 h-24 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
                  <p class="text-lg font-medium text-primary" data-i18n="simulation.in.development">Симуляция в разработке</p>
                  <p class="text-secondary" data-i18n="simulation.available.soon">Эта интерактивная симуляция будет доступна в ближайшее время</p>
                </div>
              </div>
              
                             <div class="flex justify-between items-center">
                 <button class="btn btn-secondary" onclick="window.PhysicsLabApp.navigateTo('home')">
                   <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                   </svg>
                   <span data-i18n="simulation.back.to.list">Назад к симуляциям</span>
                 </button>
                 
                 <button class="btn btn-primary" onclick="window.PhysicsLabApp.navigateTo('simulation/ohms-law')">
                   <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                   </svg>
                   <span data-i18n="simulation.try.ohms">Попробовать закон Ома</span>
                 </button>
               </div>
            </div>
          </div>
        </div>
      `;
    }
    // TODO: Apply same top padding for other simulations when their pages are added here
    
    // Применяем переводы после загрузки страницы симуляции
    setTimeout(() => {
      this.updatePageTranslations();
    }, 100);
    
    // Update mobile UI after simulation page loads
    setTimeout(() => {
      this.updateMobileUI();
    }, 200);
  }

  initOhmsLawSimulation() {
    let canvas = document.getElementById('simulation-canvas');
    if (!canvas) {
      console.error('Canvas элемент не найден');
      const container = document.getElementById('canvas-container');
      if (!container) return;
      canvas = document.createElement('canvas');
      canvas.id = 'simulation-canvas';
      canvas.width = container.clientWidth || 1200;
      canvas.height = 600;
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      canvas.style.height = '600px';
      container.appendChild(canvas);
    }

    console.log('🎯 Инициализация симуляции закона Ома...');
    
    // Импортируем и создаем симуляцию
    import('./sims/ohms-law.js').then(module => {
      try {
        const OhmsLawSimulation = module.default;
        console.log('✅ Модуль симуляции загружен');
        
        this.currentSimulation = new OhmsLawSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n
        });
        
        console.log('✅ Симуляция создана успешно');

        // Поддержка ресайза для inline-режима
        const handleResize = () => {
          const rect = canvas.getBoundingClientRect();
          canvas.width = Math.max(320, Math.floor(rect.width));
          canvas.height = Math.max(300, Math.floor(rect.height));
          if (this.currentSimulation && this.currentSimulation.resize) {
            this.currentSimulation.resize(canvas.width, canvas.height);
          }
        };
        handleResize();
        window.addEventListener('resize', handleResize);

        // Добавляем обработчики кнопок
        const startButton = document.getElementById('start-simulation');
        const resetButton = document.getElementById('reset-simulation');
        const exampleButton = document.getElementById('example-circuit');
        const openPlayerButton = document.getElementById('open-player');

        if (exampleButton) {
          exampleButton.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.createExampleCircuit();
              // Пример цепи авто-запускает симуляцию, синхронизируем кнопку Старт/Пауза
              const startButton = document.getElementById('start-simulation');
              if (startButton) this.updateStartButtonState(startButton, true);
            }
          });
        }

        if (startButton) {
          startButton.addEventListener('click', () => {
            if (this.currentSimulation) {
              if (this.currentSimulation.state.isRunning) {
                this.currentSimulation.pause();
                this.updateStartButtonState(startButton, false);
              } else {
                this.currentSimulation.start();
                this.updateStartButtonState(startButton, true);
              }
            }
          });
        }

        if (resetButton) {
          resetButton.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.reset();
              if (startButton) {
                this.updateStartButtonState(startButton, false);
              }
            }
          });
        }

        if (openPlayerButton) {
          openPlayerButton.addEventListener('click', () => {
            const url = `player.html?sim=ohms-law`;
            window.location.href = url;
          });
        }
      } catch (error) {
        console.error('❌ Ошибка при создании симуляции:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка создания симуляции: ' + error.message);
      }
    }).catch(error => {
      console.error('Ошибка загрузки симуляции:', error);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки симуляции: ' + error.message);
    });
  }

  initProjectileMotionSimulation() {
    const canvas = document.getElementById('projectile-canvas');
    if (!canvas) {
      return;
    }

    import('./sims/projectile-motion.js').then(module => {
      try {
        const ProjectileMotionSimulation = module.default;
        
        this.currentSimulation = new ProjectileMotionSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n
        });

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('start-projectile');
        const resetBtn = document.getElementById('reset-projectile');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) {
              return;
            }
            
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        } else {
          console.error('❌ Кнопка start не найдена!');
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) {
              this.updateStartButtonState(startBtn, false);
            }
          });
        }

        // Параметры: скорость
        const velRange = document.getElementById('vel-range');
        const velNumber = document.getElementById('vel-number');
        const syncVelocity = (value) => {
          velRange.value = String(value);
          velNumber.value = String(value);
          this.currentSimulation.updateParameter('initialVelocity', Number(value));
        };
        if (velRange && velNumber) {
          velRange.addEventListener('input', (e) => syncVelocity(e.target.value));
          velNumber.addEventListener('change', (e) => syncVelocity(e.target.value));
        }

        // Параметры: угол
        const angleRange = document.getElementById('angle-range');
        const angleNumber = document.getElementById('angle-number');
        const syncAngle = (value) => {
          angleRange.value = String(value);
          angleNumber.value = String(value);
          this.currentSimulation.updateParameter('angle', Number(value));
        };
        if (angleRange && angleNumber) {
          angleRange.addEventListener('input', (e) => syncAngle(e.target.value));
          angleNumber.addEventListener('change', (e) => syncAngle(e.target.value));
        }

        // Параметры: высота
        const heightRange = document.getElementById('height-range');
        const heightNumber = document.getElementById('height-number');
        const syncHeight = (value) => {
          heightRange.value = String(value);
          heightNumber.value = String(value);
          this.currentSimulation.updateParameter('height', Number(value));
        };
        if (heightRange && heightNumber) {
          heightRange.addEventListener('input', (e) => syncHeight(e.target.value));
          heightNumber.addEventListener('change', (e) => syncHeight(e.target.value));
        }

        // Синхронизируем HTML элементы с дефолтными значениями симуляции
        if (velRange && velNumber) {
          velRange.value = '20';
          velNumber.value = '20';
          this.currentSimulation.updateParameter('initialVelocity', 20);
        }
        if (angleRange && angleNumber) {
          angleRange.value = '45';
          angleNumber.value = '45';
          this.currentSimulation.updateParameter('angle', 45);
        }
        if (heightRange && heightNumber) {
          heightRange.value = '5';
          heightNumber.value = '5';
          this.currentSimulation.updateParameter('height', 5);
        }





      } catch (error) {
        console.error('Ошибка при инициализации projectile-motion:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля projectile-motion:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initDiffusionSimulation() {
    const canvas = document.getElementById('diffusion-canvas');
    if (!canvas) {
      console.error('Canvas для diffusion не найден');
      return;
    }

    import('./sims/diffusion.js').then(module => {
      try {
        const DiffusionSimulation = module.default;
        this.currentSimulation = new DiffusionSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n,
          temperatureA: 300, // 300K по умолчанию
          temperatureB: 300  // 300K по умолчанию
        });

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('diffusion-start');
        const resetBtn = document.getElementById('diffusion-reset');
        const toggleBarrierBtn = document.getElementById('diffusion-toggle-barrier');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) {
              this.updateStartButtonState(startBtn, false);
            }
          });
        }

        if (toggleBarrierBtn) {
          toggleBarrierBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            const closed = this.currentSimulation.parameters.barrierClosed;
            this.currentSimulation.updateParameter('barrierClosed', !closed);
            toggleBarrierBtn.textContent = closed ? this.i18n.t('sim.diffusion.close.partition') : this.i18n.t('sim.diffusion.open.partition');
          });
        }

        // Параметры: количества
        const numARange = document.getElementById('numA-range');
        const numANumber = document.getElementById('numA-number');
        const syncA = (value) => {
          numARange.value = String(value);
          numANumber.value = String(value);
          this.currentSimulation.updateParameter('numA', Number(value));
        };
        if (numARange && numANumber) {
          numARange.addEventListener('input', (e) => syncA(e.target.value));
          numANumber.addEventListener('change', (e) => syncA(e.target.value));
        }

        const numBRange = document.getElementById('numB-range');
        const numBNumber = document.getElementById('numB-number');
        const syncB = (value) => {
          numBRange.value = String(value);
          numBNumber.value = String(value);
          this.currentSimulation.updateParameter('numB', Number(value));
        };
        if (numBRange && numBNumber) {
          numBRange.addEventListener('input', (e) => syncB(e.target.value));
          numBNumber.addEventListener('change', (e) => syncB(e.target.value));
        }

        // Температура A
        const tempARange = document.getElementById('tempA-range');
        const tempANumber = document.getElementById('tempA-number');
        const syncTempA = (value) => {
          tempARange.value = String(value);
          tempANumber.value = String(value);
          this.currentSimulation.updateParameter('temperatureA', Number(value));
        };
        if (tempARange && tempANumber) {
          tempARange.addEventListener('input', (e) => syncTempA(e.target.value));
          tempANumber.addEventListener('change', (e) => syncTempA(e.target.value));
        }

        // Температура B
        const tempBRange = document.getElementById('tempB-range');
        const tempBNumber = document.getElementById('tempB-number');
        const syncTempB = (value) => {
          tempBRange.value = String(value);
          tempBNumber.value = String(value);
          this.currentSimulation.updateParameter('temperatureB', Number(value));
        };
        if (tempBRange && tempBNumber) {
          tempBRange.addEventListener('input', (e) => syncTempB(e.target.value));
          tempBNumber.addEventListener('change', (e) => syncTempB(e.target.value));
        }

        // Радиус частиц A
        const radiusARange = document.getElementById('radiusA-range');
        const radiusANumber = document.getElementById('radiusA-number');
        const syncRadiusA = (value) => {
          radiusARange.value = String(value);
          radiusANumber.value = String(value);
          this.currentSimulation.updateParameter('particleRadiusA', Number(value));
        };
        if (radiusARange && radiusANumber) {
          radiusARange.addEventListener('input', (e) => syncRadiusA(e.target.value));
          radiusANumber.addEventListener('change', (e) => syncRadiusA(e.target.value));
        }

        // Радиус частиц B
        const radiusBRange = document.getElementById('radiusB-range');
        const radiusBNumber = document.getElementById('radiusB-number');
        const syncRadiusB = (value) => {
          radiusBRange.value = String(value);
          radiusBNumber.value = String(value);
          this.currentSimulation.updateParameter('particleRadiusB', Number(value));
        };
        if (radiusBRange && radiusBNumber) {
          radiusBRange.addEventListener('input', (e) => syncRadiusB(e.target.value));
          radiusBNumber.addEventListener('change', (e) => syncRadiusB(e.target.value));
        }

        // Масса A
        const massARange = document.getElementById('massA-range');
        const massANumber = document.getElementById('massA-number');
        const syncMassA = (value) => {
          massARange.value = String(value);
          massANumber.value = String(value);
          this.currentSimulation.parameters.massA = Math.max(0.1, Number(value));
        };
        if (massARange && massANumber) {
          massARange.addEventListener('input', (e) => syncMassA(e.target.value));
          massANumber.addEventListener('change', (e) => syncMassA(e.target.value));
        }

        // Масса B
        const massBRange = document.getElementById('massB-range');
        const massBNumber = document.getElementById('massB-number');
        const syncMassB = (value) => {
          massBRange.value = String(value);
          massBNumber.value = String(value);
          this.currentSimulation.parameters.massB = Math.max(0.1, Number(value));
        };
        if (massBRange && massBNumber) {
          massBRange.addEventListener('input', (e) => syncMassB(e.target.value));
          massBNumber.addEventListener('change', (e) => syncMassB(e.target.value));
        }

      } catch (error) {
        console.error('Ошибка при инициализации diffusion:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля diffusion:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initPendulumSimulation() {
    const canvas = document.getElementById('pendulum-canvas');
    if (!canvas) {
      console.error('Canvas для pendulum не найден');
      return;
    }

    import('./sims/pendulum.js').then(module => {
      try {
        const PendulumSimulation = module.default;
        this.currentSimulation = new PendulumSimulation(canvas, { width: canvas.width, height: canvas.height });
        // Гарантированно не запускать автоматически: сбросим и отрендерим статично
        if (this.currentSimulation && typeof this.currentSimulation.pause === 'function') {
          this.currentSimulation.pause();
        }
        if (this.currentSimulation && typeof this.currentSimulation.reset === 'function') {
          this.currentSimulation.reset();
        }

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('pendulum-start');
        const resetBtn = document.getElementById('pendulum-reset');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
          // Убедимся, что кнопка в состоянии "Старт"
          this.updateStartButtonState(startBtn, false);
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) this.updateStartButtonState(startBtn, false);
          });
        }

        const bind = (rangeId, numId, param, map = (v)=>v) => {
          const r = document.getElementById(rangeId);
          const n = document.getElementById(numId);
          const sync = (value) => {
            r.value = String(value);
            n.value = String(value);
            this.currentSimulation.updateParameter(param, map(Number(value)));
          };
          if (r && n) {
            r.addEventListener('input', (e) => sync(e.target.value));
            n.addEventListener('change', (e) => sync(e.target.value));
          }
        };

        bind('pend-length-range','pend-length-number','length');
        bind('pend-mass-range','pend-mass-number','mass');
        bind('pend-amp-range','pend-amp-number','amplitude');
        bind('pend-damp-range','pend-damp-number','damping');
        bind('pend-g-range','pend-g-number','gravity');

        // Убираем автозапуск: маятник ждёт ручного старта

      } catch (error) {
        console.error('Ошибка при инициализации pendulum:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля pendulum:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initHiggsBosonSimulation() {
    const canvas = document.getElementById('higgs-canvas');
    if (!canvas) {
      console.error('Canvas для higgs-boson не найден');
      return;
    }

    import('./sims/higgs-boson.js').then(module => {
      try {
        const HiggsBosonSimulation = module.default;
        this.currentSimulation = new HiggsBosonSimulation(canvas, {});

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('higgs-start');
        const resetBtn = document.getElementById('higgs-reset');
        const select = document.getElementById('particle-select');
        const couplingRange = document.getElementById('coupling-range');
        const couplingNumber = document.getElementById('coupling-number');
        const cartoonToggle = document.getElementById('cartoon-toggle');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) this.updateStartButtonState(startBtn, false);
          });
        }

        if (select) {
          select.addEventListener('change', (e) => {
            const value = e.target.value;
            this.currentSimulation.updateParameter('particleType', value);
            // Для фотона обнуляем coupling-контролы визуально
            const isPhoton = value === 'photon';
            const cVal = isPhoton ? 0 : couplingRange.value;
            couplingRange.value = cVal;
            couplingNumber.value = cVal;
          });
        }

        const syncCoupling = (value) => {
          couplingRange.value = String(value);
          couplingNumber.value = String(value);
          this.currentSimulation.updateParameter('coupling', Number(value));
        };
        if (couplingRange && couplingNumber) {
          couplingRange.addEventListener('input', (e) => syncCoupling(e.target.value));
          couplingNumber.addEventListener('change', (e) => syncCoupling(e.target.value));
        }

        if (cartoonToggle) {
          cartoonToggle.addEventListener('change', (e) => {
            this.currentSimulation.updateParameter('cartoonMode', e.target.checked);
          });
        }

      } catch (error) {
        console.error('Ошибка при инициализации higgs-boson:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля higgs-boson:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initGeometricOpticsSimulation() {
    const canvas = document.getElementById('optics-canvas');
    if (!canvas) {
      console.error('Canvas для geometric-optics не найден');
      return;
    }

    import('./sims/geometric-optics.js').then(module => {
      try {
        const GeometricOpticsSimulation = module.default;
        this.currentSimulation = new GeometricOpticsSimulation(canvas, {
          i18n: this.i18n
        });

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        // Обработчики только для кнопок Start/Reset
        const startBtn = document.getElementById('optics-start');
        const resetBtn = document.getElementById('optics-reset');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) this.updateStartButtonState(startBtn, false);
          });
        }

        // Все управление теперь происходит через canvas интерфейс

        // Автозапуск
        try { this.currentSimulation.start(); if (startBtn) this.updateStartButtonState(startBtn, true); } catch {}

        // Симуляция готова к работе

      } catch (error) {
        console.error('Ошибка при инициализации geometric-optics:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля geometric-optics:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  initChargesAndFieldsSimulation() {
    const canvas = document.getElementById('charges-canvas');
    if (!canvas) {
      console.error('Canvas для charges-and-fields не найден');
      return;
    }

    import('./sims/charges-and-fields.js').then(module => {
      try {
        const ChargesAndFieldsSimulation = module.default;
        this.currentSimulation = new ChargesAndFieldsSimulation(canvas, {
				i18n: this.i18n
			});

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        // Обработчики только для кнопок Start/Reset
        const startBtn = document.getElementById('charges-start');
        const resetBtn = document.getElementById('charges-reset');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.state.isRunning) {
              this.currentSimulation.pause();
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.start();
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
          });
        }

        // Автозапуск
        try { this.currentSimulation.start(); if (startBtn) this.updateStartButtonState(startBtn, true); } catch {}

        // Симуляция готова к работе

      } catch (error) {
        console.error('Ошибка при инициализации charges-and-fields:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля charges-and-fields:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }


  initGravityOrbitsSimulation() {
    const canvas = document.getElementById('orbits-canvas');
    if (!canvas) {
      console.error('Canvas для gravity-and-orbits не найден');
      return;
    }

    import('./sims/gravity-orbits.js').then(module => {
      try {
        const GravityOrbitsSimulation = module.default;
        this.currentSimulation = new GravityOrbitsSimulation(canvas, {
          i18n: this.i18n
        });

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const startBtn = document.getElementById('orbits-start');
        const resetBtn = document.getElementById('orbits-reset');

        if (startBtn) {
          startBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            if (this.currentSimulation.parameters.isRunning) {
              this.currentSimulation.parameters.isRunning = false;
              this.updateStartButtonState(startBtn, false);
            } else {
              this.currentSimulation.parameters.isRunning = true;
              this.updateStartButtonState(startBtn, true);
            }
          });
        }

        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (!this.currentSimulation) return;
            this.currentSimulation.reset();
            if (startBtn) this.updateStartButtonState(startBtn, false);
          });
        }

        // Автозапуск
        try { 
          this.currentSimulation.parameters.isRunning = true; 
          if (startBtn) this.updateStartButtonState(startBtn, true); 
        } catch {}

      } catch (error) {
        console.error('Ошибка при инициализации gravity-and-orbits:', error);
        this.showErrorOnCanvas(canvas, 'Ошибка: ' + error.message);
      }
    }).catch(err => {
      console.error('Ошибка загрузки модуля gravity-orbits:', err);
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки модуля: ' + err.message);
    });
  }

  showErrorOnCanvas(canvas, message) {
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#dc3545';
    ctx.font = '20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Ошибка симуляции', canvas.width / 2, canvas.height / 2 - 30);
    ctx.font = '14px Arial';
    ctx.fillText(message, canvas.width / 2, canvas.height / 2);
    ctx.fillText('Попробуйте обновить страницу', canvas.width / 2, canvas.height / 2 + 30);
  }

  async loadAboutPage() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Временно отключаем плавную прокрутку и мгновенно переходим вверх
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Прокручиваем страницу вверх при загрузке страницы "О проекте" (без анимации)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Для Safari
    
    // Восстанавливаем плавную прокрутку
    html.style.scrollBehavior = originalScrollBehavior;

    // Остановить текущую симуляцию при переходе на страницу "О проекте"
    if (this.currentSimulation && typeof this.currentSimulation.destroy === 'function') {
      try {
        this.currentSimulation.destroy();
      } catch (e) {
        console.warn('Не удалось корректно уничтожить симуляцию при переходе на About:', e);
      }
      this.currentSimulation = null;
    }

    try {
      // Импортируем и используем AboutPage из platform/page-about.js
      const aboutPageModule = await import('./platform/page-about.js');
      const AboutPage = aboutPageModule.default;
      
      if (AboutPage) {
        const aboutPage = new AboutPage();
        await aboutPage.init();
        await aboutPage.render();
        
        // Применяем переводы к загруженному контенту
        if (this.i18n) {
          setTimeout(() => {
            this.updatePageTranslations();
          }, 100);
        }
      } else {
        console.error('AboutPage не найден');
        this.loadAboutPageFallback();
      }
    } catch (error) {
      console.error('Ошибка загрузки AboutPage:', error);
      this.loadAboutPageFallback();
    }
  }

  loadAboutPageFallback() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    mainContent.innerHTML = `
      <!-- Fallback страница "О проекте" -->
      <div class="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        
        <!-- Анимированный фон -->
        <div class="absolute inset-0">
          <div class="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
          <div class="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        </div>

        <!-- Основной контент -->
        <div class="relative z-10">
          
          <!-- Hero Section -->
          <section class="page-hero px-4">
            <div class="max-w-7xl mx-auto">
              <div class="text-center mb-8">
                <h1 class="text-6xl md:text-7xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent mb-6 leading-tight">
                  О проекте Илим Lab
                </h1>
                
                <p class="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                  Революционная интерактивная образовательная платформа для изучения физики через 
                  <span class="text-blue-400 font-semibold">продвинутые симуляции</span> и 
                  <span class="text-purple-400 font-semibold">современные технологии</span>
                </p>
              </div>
            </div>
          </section>

          <!-- Основные принципы -->
          <section class="py-12 px-4">
            <div class="max-w-7xl mx-auto">
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                <!-- Миссия -->
                <div class="group relative">
                  <div class="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                  <div class="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-blue-400/40 transition-all duration-500 h-full">
                    <div class="flex items-center mb-6">
                      <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                      </div>
                      <h3 class="text-2xl font-bold text-white">Наша миссия</h3>
                    </div>
                    <p class="text-gray-300 leading-relaxed text-lg">
                      Сделать изучение физики <span class="text-blue-300 font-semibold">доступным</span>, 
                      <span class="text-purple-300 font-semibold">увлекательным</span> и 
                      <span class="text-indigo-300 font-semibold">эффективным</span> для всех студентов и школьников, 
                      независимо от их уровня подготовки, используя передовые технологии и инновационный дизайн.
                    </p>
                  </div>
                </div>

                <!-- Видение -->
                <div class="group relative">
                  <div class="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                  <div class="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-emerald-400/40 transition-all duration-500 h-full">
                    <div class="flex items-center mb-6">
                      <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                        </svg>
                      </div>
                      <h3 class="text-2xl font-bold text-white">Наше видение</h3>
                    </div>
                    <p class="text-gray-300 leading-relaxed text-lg">
                      Мы видим будущее, где каждый студент может <span class="text-emerald-300 font-semibold">экспериментировать</span> 
                      с физическими законами в безопасной среде, получая мгновенную обратную связь и 
                      <span class="text-teal-300 font-semibold">глубокое понимание</span> сложных концепций.
                    </p>
                  </div>
                </div>

                <!-- Ценности -->
                <div class="group relative">
                  <div class="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                  <div class="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-purple-400/40 transition-all duration-500 h-full">
                    <div class="flex items-center mb-6">
                      <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                      </div>
                      <h3 class="text-2xl font-bold text-white">Наши ценности</h3>
                    </div>
                    <p class="text-gray-300 leading-relaxed text-lg">
                      <span class="text-purple-300 font-semibold">Инновации</span>, 
                      <span class="text-pink-300 font-semibold">качество</span> и 
                      <span class="text-indigo-300 font-semibold">доступность</span> - 
                      три столпа, на которых строится наша платформа для создания лучшего образовательного опыта.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Статистика -->
          <section class="py-12 px-4">
            <div class="max-w-7xl mx-auto">

              
              <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div class="group text-center">
                  <div class="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-400/30 hover:border-blue-300/50 transition-all duration-500">
                    <div class="text-5xl font-bold text-blue-600 mb-3 group-hover:scale-110 transition-transform duration-300">9+</div>
                    <div class="text-blue-600 font-semibold text-lg">Симуляций</div>
                    <div class="text-blue-500 text-sm mt-2">Физических процессов</div>
                  </div>
                </div>
                
                <div class="group text-center">
                  <div class="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-xl rounded-3xl p-8 border border-emerald-400/30 hover:border-emerald-300/50 transition-all duration-500">
                    <div class="text-5xl font-bold text-emerald-600 mb-3 group-hover:scale-110 transition-transform duration-300">3</div>
                    <div class="text-emerald-600 font-semibold text-lg">Языка</div>
                    <div class="text-emerald-500 text-sm mt-2">Поддержка</div>
                  </div>
                </div>
                
                <div class="group text-center">
                  <div class="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-xl rounded-3xl p-8 border border-purple-400/30 hover:border-purple-300/50 transition-all duration-500">
                    <div class="text-5xl font-bold text-purple-600 mb-3 group-hover:scale-110 transition-transform duration-300">100%</div>
                    <div class="text-purple-600 font-semibold text-lg">Интерактивность</div>
                    <div class="text-purple-500 text-sm mt-2">Полное погружение</div>
                  </div>
                </div>
                
                <div class="group text-center">
                  <div class="bg-gradient-to-br from-orange-500/20 to-orange-600/20 backdrop-blur-xl rounded-3xl p-8 border border-orange-400/30 hover:border-orange-300/50 transition-all duration-500">
                    <div class="text-5xl font-bold text-orange-600 mb-3 group-hover:scale-110 transition-transform duration-300">24/7</div>
                    <div class="text-orange-600 font-semibold text-lg">Доступность</div>
                    <div class="text-orange-500 text-sm mt-2">В любое время</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- CTA Section -->
          <section class="py-12 px-4">
            <div class="max-w-7xl mx-auto">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
                <div class="relative bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-xl rounded-3xl p-12 border border-blue-400/30 text-center">
                  <h2 class="text-5xl md:text-6xl font-bold text-white mb-6">
                    Готовы начать путешествие?
                  </h2>

                  
                  <div class="flex flex-col sm:flex-row gap-6 justify-center">
                    <button class="btn btn-primary btn-lg" onclick="window.PhysicsLabApp.navigateTo('home')">
                      Вернуться на главную
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    `;

    // Применяем переводы к fallback контенту
    if (this.i18n) {
      setTimeout(() => {
        this.updatePageTranslations();
      }, 100);
    }
  }

     navigateTo(path, scrollToId = null) {
    console.log('🚀 Навигация к маршруту:', path, 'с прокруткой к:', scrollToId);
    
    // Проверяем, что приложение инициализировано
    if (!this.isInitialized) {
      console.warn('⚠️ Приложение еще не инициализировано, откладываем навигацию');
      setTimeout(() => this.navigateTo(path, scrollToId), 100);
      return;
    }
    
    // Close mobile menu if open
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }

    // Update URL without reloading
    const currentHash = window.location.hash.slice(1);
    const newHash = path === 'home' ? '/' : `/${path}`;
    
    // Если hash уже установлен на нужное значение, принудительно вызываем обработчик
    if (currentHash === newHash) {
      console.log('📍 Hash уже установлен, принудительно вызываем handleRouteChange');
      this.handleRouteChange();
      return;
    }
    
    if (path === 'home') {
      window.location.hash = '/';
    } else {
      window.location.hash = `/${path}`;
    }
    
    // Принудительно вызываем обработчик маршрута
    // Не полагаемся только на событие hashchange, так как оно может не сработать
    this.handleRouteChange();
    
    // Handle scroll after navigation, especially for home page sections
    if (path === 'home' && scrollToId) {
      console.log('🎯 Начинаем обработку прокрутки к:', scrollToId);
      
      // Даем время контенту загрузиться и проверяем несколько раз
      let attempts = 0;
      const maxAttempts = 10;
      
      const attemptScroll = () => {
        attempts++;
        console.log(`🎯 Попытка прокрутки ${attempts}/${maxAttempts}`);
        
        const targetElement = document.getElementById(scrollToId);
        console.log('🔍 Ищем элемент с ID:', scrollToId);
        console.log('🔍 Найденный элемент:', targetElement);
        
        if (targetElement) {
          console.log('🎯 Найден элемент для прокрутки:', scrollToId);
          console.log('🎯 Позиция элемента:', targetElement.getBoundingClientRect());
          console.log('🎯 Текущая позиция скролла:', window.pageYOffset);
          
          // Прокручиваем к элементу с плавной анимацией
          try {
            targetElement.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
            console.log('🎯 Прокрутка scrollIntoView выполнена');
          } catch (error) {
            console.log('⚠️ scrollIntoView не сработал, используем альтернативный метод:', error);
            // Альтернативный метод прокрутки для случаев, когда scrollIntoView не работает
            const elementTop = targetElement.offsetTop;
            const headerHeight = 80; // Примерная высота хедера
            window.scrollTo({
              top: elementTop - headerHeight,
              behavior: 'smooth'
            });
            console.log('🎯 Прокрутка альтернативным методом выполнена');
          }
          
          console.log('🎯 Прокрутка выполнена, новая позиция скролла:', window.pageYOffset);
          
        } else if (attempts < maxAttempts) {
          console.log(`⚠️ Элемент не найден, попытка ${attempts + 1} через 50ms`);
          setTimeout(attemptScroll, 50);
        } else {
          console.warn('⚠️ Элемент для прокрутки не найден после всех попыток:', scrollToId);
          console.warn('⚠️ Доступные элементы с ID:', Array.from(document.querySelectorAll('[id]')).map(el => el.id));
        }
      };
      
      // Начинаем попытки прокрутки
      setTimeout(attemptScroll, 100);
    }
  }

  handleThemeToggle() {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', this.theme);
    this.applyTheme();
    
    // Уведомления отключены
  }

  applyTheme() {
    try {
      document.documentElement.setAttribute('data-theme', this.theme);
      
      const themeIcon = document.getElementById('theme-icon');
      if (themeIcon) {
        themeIcon.innerHTML = this.theme === 'dark' 
          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>'
          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>';
      }
    } catch (error) {
      console.error('Failed to apply theme:', error);
    }
  }

  handleLanguageChange(newLanguage) {
    this.language = newLanguage;
    localStorage.setItem('language', this.language);
    
    // Обновляем i18n систему
    if (this.i18n) {
      this.i18n.setLanguage(this.language);
    }
    
    this.applyLanguage();
    
    // Обновляем переводы с небольшой задержкой
    setTimeout(() => {
      this.updatePageTranslations();
    }, 50);
    
    // Уведомление о смене языка убрано по запросу пользователя
  }

  applyLanguage() {
    try {
      // Обновляем селекторы языков
      const langSelect = document.getElementById('lang-select');
      const mobileLangSelect = document.getElementById('mobile-lang-select');
      
      if (langSelect) langSelect.value = this.language;
      if (mobileLangSelect) mobileLangSelect.value = this.language;
    } catch (error) {
      console.error('Failed to apply language:', error);
    }
  }

  updatePageTranslations() {
    if (!this.i18n) return;
    
    try {
      console.log('🔄 Обновляем переводы для языка:', this.language);
      
      // Обновляем элементы с data-i18n атрибутами
      const elementsWithI18n = document.querySelectorAll('[data-i18n]');
      console.log('📝 Найдено элементов с data-i18n:', elementsWithI18n.length);
      
      elementsWithI18n.forEach(element => {
        const key = element.getAttribute('data-i18n');
        const translation = this.i18n.t(key);
        console.log(`🔍 Ключ: ${key}, Перевод: ${translation}`);
        if (translation && translation !== key) {
          element.textContent = translation;
          console.log(`✅ Обновлен ${key}:`, translation.substring(0, 50) + '...');
        } else {
          console.log(`⚠️ Не найден перевод для ${key}`);
        }
      });

      // Обновляем заголовки страницы
      const titleElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      titleElements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        if (key) {
          const translation = this.i18n.t(key);
          if (translation && translation !== key) {
            element.textContent = translation;
          }
        }
      });

      // Обновляем кнопки
      const buttonElements = document.querySelectorAll('button');
      buttonElements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        if (key) {
          const translation = this.i18n.t(key);
          if (translation && translation !== key) {
            element.textContent = translation;
          }
        }
      });

      // Обновляем ссылки
      const linkElements = document.querySelectorAll('a');
      linkElements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        if (key) {
          const translation = this.i18n.t(key);
          if (translation && translation !== key) {
            element.textContent = translation;
          }
        }
      });

      // Обновляем переводы в текущей симуляции
      if (this.currentSimulation && typeof this.currentSimulation.updateTranslations === 'function') {
        console.log('🔄 Обновляем переводы в симуляции');
        this.currentSimulation.updateTranslations();
      }

      console.log('✅ Переводы обновлены');
    } catch (error) {
      console.error('❌ Ошибка обновления переводов:', error);
    }
  }

  handleMobileMenu() {
    try {
      const mobileMenu = document.getElementById('mobile-menu');
      
      if (!mobileMenu) {
        console.error('Mobile menu element not found');
        return;
      }
      
      if (mobileMenu.classList.contains('open')) {
        mobileMenu.classList.remove('open');
      } else {
        mobileMenu.classList.add('open');
      }
    } catch (error) {
      console.error('Failed to handle mobile menu:', error);
    }
  }

  async hideLoadingScreen() {
    try {
      const loading = document.getElementById('loading');
      const mainApp = document.getElementById('main-app');
      
      if (loading && mainApp) {
        // Быстрое скрытие экрана загрузки
        loading.style.transition = 'opacity 0.2s ease-out, visibility 0.2s ease-out';
        loading.style.opacity = '0';
        loading.style.visibility = 'hidden';
        
        // Быстрое появление основного приложения
        mainApp.style.display = 'block';
        mainApp.style.opacity = '1';
        mainApp.style.transform = 'translateY(0)';
        mainApp.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
    } catch (error) {
      console.error('Failed to hide loading screen:', error);
    }
  }

  showErrorScreen(error) {
    try {
      const loading = document.getElementById('loading');
      if (loading) {
        loading.innerHTML = `
          <div class="loading-content">
            <div class="w-24 h-24 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-12 h-12 text-error-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-white mb-4">Ошибка инициализации</h1>
            <p class="text-white opacity-90 mb-6">Не удалось загрузить приложение</p>
            <button class="btn bg-white text-primary-600 hover:bg-gray-100" onclick="window.location.reload()">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Попробовать снова
            </button>
          </div>
        `;
      }
    } catch (error) {
      console.error('Failed to show error screen:', error);
    }
  }

  initBuildNucleusSimulation() {
    const canvas = document.getElementById('nucleus-canvas');
    if (!canvas) {
      console.error('Canvas для build-nucleus не найден');
      return;
    }

    import('./sims/build-nucleus.js').then(module => {
      try {
        console.log('🔧 Loading BuildNucleusSimulation module...');
        const BuildNucleusSimulation = module.default;
        console.log('🔧 BuildNucleusSimulation class loaded:', BuildNucleusSimulation);
        
        this.currentSimulation = new BuildNucleusSimulation(canvas, {
          width: canvas.width,
          height: canvas.height,
          i18n: this.i18n
        });
        console.log('🔧 BuildNucleusSimulation instance created:', this.currentSimulation);

        // Update mobile UI after simulation initialization
        this.updateMobileUI();

        const resetBtn = document.getElementById('nucleus-reset');
        const addProtonBtn = document.getElementById('add-proton');
        const removeProtonBtn = document.getElementById('remove-proton');
        const addNeutronBtn = document.getElementById('add-neutron');
        const removeNeutronBtn = document.getElementById('remove-neutron');
        const addPairBtn = document.getElementById('add-pair');
        const removePairBtn = document.getElementById('remove-pair');
        const toggleElectronCloud = document.getElementById('toggle-electron-cloud');
        const protonCount = document.getElementById('proton-count');
        const neutronCount = document.getElementById('neutron-count');
        
        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.reset();
              this.updateNucleonCounts();
            }
          });
        }
        
        if (addProtonBtn) {
          addProtonBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.addProton(this.currentSimulation.centerX, this.currentSimulation.centerY);
              this.updateNucleonCounts();
            }
          });
        }
        
        if (removeProtonBtn) {
          removeProtonBtn.addEventListener('click', () => {
            if (this.currentSimulation && this.currentSimulation.protons > 0) {
              // Remove the last proton
              const protons = this.currentSimulation.nucleons.filter(n => n.type === 'proton');
              if (protons.length > 0) {
                const lastProton = protons[protons.length - 1];
                this.currentSimulation.removeNucleon(lastProton);
                this.updateNucleonCounts();
              }
            }
          });
        }
        
        if (addNeutronBtn) {
          addNeutronBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              this.currentSimulation.addNeutron(this.currentSimulation.centerX, this.currentSimulation.centerY);
              this.updateNucleonCounts();
            }
          });
        }
        
        if (removeNeutronBtn) {
          removeNeutronBtn.addEventListener('click', () => {
            if (this.currentSimulation && this.currentSimulation.neutrons > 0) {
              // Remove the last neutron
              const neutrons = this.currentSimulation.nucleons.filter(n => n.type === 'neutron');
              if (neutrons.length > 0) {
                const lastNeutron = neutrons[neutrons.length - 1];
                this.currentSimulation.removeNucleon(lastNeutron);
                this.updateNucleonCounts();
              }
            }
          });
        }
        
        if (addPairBtn) {
          addPairBtn.addEventListener('click', () => {
            if (this.currentSimulation) {
              // Add both proton and neutron simultaneously
              this.currentSimulation.addProton(this.currentSimulation.centerX, this.currentSimulation.centerY);
              this.currentSimulation.addNeutron(this.currentSimulation.centerX, this.currentSimulation.centerY);
              this.updateNucleonCounts();
            }
          });
        }
        
        if (removePairBtn) {
          removePairBtn.addEventListener('click', () => {
            if (this.currentSimulation && this.currentSimulation.protons > 0 && this.currentSimulation.neutrons > 0) {
              // Remove both proton and neutron simultaneously
              const protons = this.currentSimulation.nucleons.filter(n => n.type === 'proton');
              const neutrons = this.currentSimulation.nucleons.filter(n => n.type === 'neutron');
              if (protons.length > 0 && neutrons.length > 0) {
                const lastProton = protons[protons.length - 1];
                const lastNeutron = neutrons[neutrons.length - 1];
                this.currentSimulation.removeNucleon(lastProton);
                this.currentSimulation.removeNucleon(lastNeutron);
                this.updateNucleonCounts();
              }
            }
          });
        }
        
        if (toggleElectronCloud) {
          toggleElectronCloud.addEventListener('change', () => {
            if (this.currentSimulation) {
              this.currentSimulation.toggleElectronCloud();
            }
          });
        }
        
        // Update nucleon counts
        this.updateNucleonCounts = () => {
          if (this.currentSimulation && protonCount && neutronCount) {
            protonCount.textContent = this.currentSimulation.protons;
            neutronCount.textContent = this.currentSimulation.neutrons;
          }
        };
        
        // Initial count update
        this.updateNucleonCounts();

        console.log('✅ Симуляция "Сборка ядра" инициализирована');
      } catch (error) {
        console.error('❌ Ошибка при создании симуляции "Сборка ядра":', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          canvas: canvas,
          module: module
        });
        this.showErrorOnCanvas(canvas, 'Ошибка создания симуляции: ' + error.message);
      }
    }).catch(error => {
      console.error('❌ Ошибка загрузки симуляции "Сборка ядра":', error);
      console.error('Import error details:', {
        message: error.message,
        stack: error.stack
      });
      this.showErrorOnCanvas(canvas, 'Ошибка загрузки симуляции: ' + error.message);
    });
  }

  showNotification(message, type = 'info') {
    // Уведомления отключены
    return null;
  }

  initEvents() {
    // Theme toggle
    document.getElementById('theme-toggle').addEventListener('click', () => this.toggleTheme());
    document.getElementById('mobile-theme-toggle').addEventListener('click', () => this.toggleTheme());
    
    // Language selector
    document.getElementById('lang-select').addEventListener('change', (e) => this.handleLanguageChange(e.target.value));
    document.getElementById('mobile-lang-select').addEventListener('change', (e) => this.handleLanguageChange(e.target.value));
    
    // Mobile menu
    document.getElementById('menu-toggle').addEventListener('click', () => this.handleMobileMenu());
    document.getElementById('close-menu').addEventListener('click', () => this.handleMobileMenu());
    
    // Navigation
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-route]')) {
        e.preventDefault();
        const route = e.target.getAttribute('data-route');
        this.navigateTo(route);
        
        // Close mobile menu when navigation link is clicked
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu && mobileMenu.classList.contains('open')) {
          mobileMenu.classList.remove('open');
        }
      }
    });
    
    // Fullscreen simulation controls
    document.getElementById('overlay-close').addEventListener('click', () => this.exitFullscreenMode());
    document.getElementById('mobile-launch-btn').addEventListener('click', () => this.enterFullscreenMode());
    
    // Handle window resize for canvas resizing
    window.addEventListener('resize', () => this.handleCanvasResize());
    
    // Handle escape key to exit fullscreen
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isFullscreenMode) {
        this.exitFullscreenMode();
      }
    });
  }

  // Fullscreen simulation methods
  enterFullscreenMode() {
    if (!this.currentSimulation) return;
    
    this.isFullscreenMode = true;
    const overlay = document.getElementById('simulation-overlay');
    const overlayCanvas = document.getElementById('overlay-canvas-container');
    const overlayControls = document.getElementById('overlay-controls');
    const overlayTitle = document.getElementById('overlay-simulation-title');
    const mobileLaunchBtn = document.getElementById('mobile-launch-btn');
    
    // Store original containers
    this.originalCanvasContainer = this.currentSimulation.canvas.parentElement;
    this.originalControlsContainer = document.querySelector('.simulation-controls');
    
    // Update overlay title
    const simulationName = this.getCurrentSimulationName();
    overlayTitle.textContent = simulationName;
    
    // Move canvas to overlay
    if (this.currentSimulation.canvas) {
      overlayCanvas.appendChild(this.currentSimulation.canvas);
    }
    
    // Move controls to overlay
    if (this.originalControlsContainer) {
      overlayControls.appendChild(this.originalControlsContainer);
    }
    
    // Show overlay
    overlay.classList.add('active');
    
    // Hide mobile launch button
    mobileLaunchBtn.style.display = 'none';
    
    // Update simulation canvas size
    if (this.currentSimulation.resizeCanvas) {
      this.currentSimulation.resizeCanvas();
    }
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  exitFullscreenMode() {
    if (!this.isFullscreenMode) return;
    
    this.isFullscreenMode = false;
    const overlay = document.getElementById('simulation-overlay');
    const overlayCanvas = document.getElementById('overlay-canvas-container');
    const overlayControls = document.getElementById('overlay-controls');
    const mobileLaunchBtn = document.getElementById('mobile-launch-btn');
    
    // Move canvas back to original container
    if (this.currentSimulation.canvas && this.originalCanvasContainer) {
      this.originalCanvasContainer.appendChild(this.currentSimulation.canvas);
    }
    
    // Move controls back to original container
    if (this.originalControlsContainer) {
      const simulationMain = document.querySelector('.simulation-main');
      if (simulationMain) {
        simulationMain.appendChild(this.originalControlsContainer);
      }
    }
    
    // Hide overlay
    overlay.classList.remove('active');
    
    // Show mobile launch button if on mobile
    if (window.innerWidth <= 768) {
      mobileLaunchBtn.style.display = 'flex';
    }
    
    // Update simulation canvas size
    if (this.currentSimulation.resizeCanvas) {
      this.currentSimulation.resizeCanvas();
    }
    
    // Restore body scroll
    document.body.style.overflow = '';
  }

  getCurrentSimulationName() {
    const simulationNames = {
      'ohms-law': 'Закон Ома',
      'projectile-motion': 'Движение снаряда',
      'diffusion': 'Диффузия',
      'pendulum': 'Маятник',
      'higgs-boson': 'Бозон Хиггса',
      'geometric-optics': 'Геометрическая оптика',
      'gravity-and-orbits': 'Гравитация и орбиты',
      'interference-waves': 'Интерференция и волны',
      'build-nucleus': 'Сборка ядра'
    };
    
    const currentRoute = this.currentRoute;
    return simulationNames[currentRoute] || 'Симуляция';
  }

  // Mobile detection and optimization
  isMobileDevice() {
    return window.innerWidth <= 768 || 
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  updateMobileUI() {
    const mobileLaunchBtn = document.getElementById('mobile-launch-btn');
    
    if (this.isMobileDevice() && this.currentSimulation && !this.isFullscreenMode) {
      mobileLaunchBtn.style.display = 'flex';
    } else {
      mobileLaunchBtn.style.display = 'none';
    }
  }

  async loadDemoPage() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Временно отключаем плавную прокрутку и мгновенно переходим вверх
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Прокручиваем страницу вверх при загрузке страницы демо (без анимации)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Для Safari
    
    // Восстанавливаем плавную прокрутку
    html.style.scrollBehavior = originalScrollBehavior;

    // Остановить текущую симуляцию при переходе на страницу демо
    if (this.currentSimulation && typeof this.currentSimulation.destroy === 'function') {
      try {
        this.currentSimulation.destroy();
      } catch (e) {
        console.warn('Не удалось корректно уничтожить симуляцию при переходе на Demo:', e);
      }
      this.currentSimulation = null;
    }

    // Импортируем и загружаем страницу демо
    try {
      const { loadDemoPage } = await import('./platform/page-demo.js');
      loadDemoPage();
    } catch (error) {
      console.error('Ошибка при загрузке страницы демо:', error);
      // Fallback на главную страницу
      await this.loadHomePage();
    }
  }

  async loadContactPage() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    // Временно отключаем плавную прокрутку и мгновенно переходим вверх
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Прокручиваем страницу вверх при загрузке страницы контактов (без анимации)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Для Safari
    
    // Восстанавливаем плавную прокрутку
    html.style.scrollBehavior = originalScrollBehavior;

    // Остановить текущую симуляцию при переходе на страницу контактов
    if (this.currentSimulation && typeof this.currentSimulation.destroy === 'function') {
      try {
        this.currentSimulation.destroy();
      } catch (e) {
        console.warn('Не удалось корректно уничтожить симуляцию при переходе на страницу контактов:', e);
      }
      this.currentSimulation = null;
    }

    // Импортируем и загружаем страницу контактов
    try {
      const { loadContactPage } = await import('./platform/page-contact.js');
      const contactHTML = loadContactPage();
      mainContent.innerHTML = contactHTML;
      
      // Применяем переводы к загруженному контенту
      if (this.i18n) {
        setTimeout(() => {
          this.updatePageTranslations();
        }, 100);
      }
    } catch (error) {
      console.error('Ошибка при загрузке страницы контактов:', error);
      // Fallback на главную страницу
      await this.loadHomePage();
    }
  }

  forceWhiteHeaders() {
    // Принудительно применяем правильные стили к заголовкам в зависимости от темы
    setTimeout(() => {
      const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
      const heroTitle = document.querySelector('.hero-title');
      const sectionTitles = document.querySelectorAll('.section-title');
      const sectionHeaders = document.querySelectorAll('.section-header h2');
      
      if (isDarkTheme) {
        // Темная тема - белый цвет
        if (heroTitle) {
          heroTitle.style.color = '#ffffff';
          heroTitle.style.webkitTextFillColor = '#ffffff';
          heroTitle.style.background = 'none';
          heroTitle.style.webkitBackgroundClip = 'unset';
          heroTitle.style.backgroundClip = 'unset';
        }
        
        sectionTitles.forEach(title => {
          title.style.color = '#ffffff';
          title.style.webkitTextFillColor = '#ffffff';
          title.style.background = 'none';
          title.style.webkitBackgroundClip = 'unset';
          title.style.backgroundClip = 'unset';
        });
        
        sectionHeaders.forEach(header => {
          header.style.color = '#ffffff';
          header.style.webkitTextFillColor = '#ffffff';
          header.style.background = 'none';
          header.style.webkitBackgroundClip = 'unset';
          header.style.backgroundClip = 'unset';
        });
      } else {
        // Светлая тема - градиентный текст
        if (heroTitle) {
          heroTitle.style.color = 'transparent';
          heroTitle.style.webkitTextFillColor = 'transparent';
          heroTitle.style.background = 'linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 100%)';
          heroTitle.style.webkitBackgroundClip = 'text';
          heroTitle.style.backgroundClip = 'text';
        }
        
        sectionTitles.forEach(title => {
          title.style.color = 'var(--primary-600)';
          title.style.webkitTextFillColor = 'unset';
          title.style.background = 'none';
          title.style.webkitBackgroundClip = 'unset';
          title.style.backgroundClip = 'unset';
        });
        
        sectionHeaders.forEach(header => {
          header.style.color = 'transparent';
          header.style.webkitTextFillColor = 'transparent';
          header.style.background = 'linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 100%)';
          header.style.webkitBackgroundClip = 'text';
          header.style.backgroundClip = 'text';
        });
      }
    }, 100);
  }

  /**
   * Открыть лабораторную работу для симуляции
   */
  async openLabWork(simulationId) {
    // Динамически импортируем модуль
    try {
      const { default: labWorkModal } = await import('./core/lab-work-modal.js');
      await labWorkModal.open(simulationId);
    } catch (error) {
      console.error('Ошибка загрузки модуля лабораторных работ:', error);
      alert('Не удалось загрузить лабораторную работу. Попробуйте позже.');
    }
  }

  /**
   * Загрузить страницу авторизации
   */
  async loadAuthPage() {
    try {
      const { default: AuthPage } = await import('./platform/page-auth.js');
      const authPage = new AuthPage();
      window.authPage = authPage; // Для доступа из onclick
      await authPage.render();
    } catch (error) {
      console.error('Ошибка загрузки страницы авторизации:', error);
    }
  }

  /**
   * Загрузить кабинет учителя
   */
  async loadTeacherDashboardPage() {
    try {
      const { default: TeacherDashboardPage } = await import('./platform/page-teacher-dashboard.js');
      const dashboard = new TeacherDashboardPage();
      window.teacherDashboard = dashboard; // Для доступа из onclick
      await dashboard.render();
    } catch (error) {
      console.error('Ошибка загрузки кабинета учителя:', error);
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  const app = new PhysicsLabApp();
  
  // Сразу делаем app доступным глобально, даже до полной инициализации
  // Это позволяет кнопкам работать сразу после загрузки страницы
  window.PhysicsLabApp = app;
  
  try {
    await app.init();
  } catch (error) {
    console.error('Failed to initialize app:', error);
  }
});

export default PhysicsLabApp;
